import _ from "lodash";
import moment from "moment";

import ACLStore from "lib/aclStore";
import { colors, FAMILY_INVITE_EMAIL_LANGUAGE_OPTIONS } from "Constants";
import {
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_UBD,
  CURRICULUM_TYPE_DP,
} from "Constants/stringConstants";
import { ENABLE_USER_ACADEMIC_YEAR_ARCHIVAL_STATUS } from "Constants/permissionConstants";

import { getMemoizedLangFunction } from "Utils/GetMemoizedLangFunction";
import {
  getFormattedUserName,
  getUserSortOptions,
} from "Utils/GetFormattedUserName";

import { getIsLearningCoursesEnabled } from "Teacher/routes/LearningCourses/modules/Utils";
import { getUiNames } from "Integrations/modules/IntegrationsUtils";
import { getUserInfo } from "Login/modules/LoginModule";
import {
  getStudentAlertsNomenclature,
  isStudentAlertTypeMappingFeatureEnabled,
} from "../routes/StudentAlertMapping/modules/Utils";
import { isRnpMappingFeatureEnabled } from "../routes/RolesPermissionsMapping/modules/Utils";
import { getAvailableServicesFromCache } from "../../../modules/SyncGraphqlHelpers";

export const FIELD_KEY_GRADE_MAPPING = "gradeMapping";
export const FIELD_KEY_SUBJECT_MAPPING = "subjectMapping";

export const PARENT_SYNC_ENABLED_SERVICES_LIST = [
  "WONDE",
  "VERACROSS",
  "VERACROSSV3",
  "QUICKSCHOOLS",
  "LEX",
  "OPENAPPLY",
  "POWERSCHOOL",
  "COMPASS",
  "SCHOOLBASE",
  "DEMOSIS",
  "CLICKEDU",
  "EDUKA",
  "CLEVER",
  "EDADMIN",
  "ISAMS",
  "BLACKBAUD",
  "SYNERGETIC",
  "SENTRAL",
  "TASS",
  "CIVICA",
  "HUBMIS",
  "GENESIS",
  "FACTS",
  "CLASSTER",
  "EDSEMBLI",
  "POWERSCHOOLV2",
  "D6",
  "REALTIME",
  "MSAOS",
  "ONESCHOOL",
];

const COURSE_SYNC_ENABLED_SERVICES_LIST = [
  "WONDE",
  "VERACROSS",
  "VERACROSSV3",
  "QUICKSCHOOLS",
  "CLASSLINK",
  "ISAMS",
  "LEX",
  "POWERSCHOOL",
  "CLEVER",
  "ENS",
  "COMPASS",
  "DEMOSIS",
  "SCHOOLBASE",
  "CLICKEDU",
  "EDADMIN",
  "BLACKBAUD",
  "SYNERGETIC",
  "SENTRAL",
  "TASS",
  "CIVICA",
  "GENESIS",
  "FACTS",
  "CLASSTER",
  "EDSEMBLI",
  "POWERSCHOOLV2",
  "HUBMIS",
  "D6",
  "REALTIME",
  "MSAOS",
  "ONESCHOOL",
];

const STAFF_SYNC_ENABLED_SERVICES_LIST = [
  "WONDE",
  "VERACROSS",
  "VERACROSSV3",
  "QUICKSCHOOLS",
  "CLASSLINK",
  "ISAMS",
  "LEX",
  "POWERSCHOOL",
  "CLEVER",
  "ENS",
  "COMPASS",
  "SCHOOLBASE",
  "DEMOSIS",
  "CLICKEDU",
  "EDADMIN",
  "BLACKBAUD",
  "SYNERGETIC",
  "SENTRAL",
  "TASS",
  "CIVICA",
  "HUBMIS",
  "GENESIS",
  "FACTS",
  "CLASSTER",
  "EDSEMBLI",
  "POWERSCHOOLV2",
  "D6",
  "REALTIME",
  "MSAOS",
  "ONESCHOOL",
];

export const syncModuleRoutes = {
  jobs: "dashboard",
  gradesMapping: "grades-mapping",
  termMapping: "term-mapping",
  subjectMapping: "subjects-mapping",
  manageCredentials: "manage-credentials",
  excludeToddleData: "toddle-exclusion",
  excludeSisData: "sis-exclusion",
  mapExistingData: "existing-roster-mapping",
  syncSettings: "settings",
  studentAlertTypeMapping: "student-alert-mapping",
  rolesPermissionsMapping: "roles-permissions-mapping",
};

export const getIsCombineClassesFlowEnabled = () => {
  return ACLStore.can("Common:IntegrationsCombineClassesSync");
};

export const showFamilyMemberTab = ({ service }) => {
  const isClassLinkPermEnabled = ACLStore.can(
    "FeatureFlag:ClassLinkParentSyncEnabled"
  );

  if (isClassLinkPermEnabled) {
    PARENT_SYNC_ENABLED_SERVICES_LIST.push("CLASSLINK");
  }

  return _.includes(PARENT_SYNC_ENABLED_SERVICES_LIST, service);
};

const courseSortOptions = [
  {
    key: "CLASS_NAME_AZ",
    label: "common:class_name_az",
    direction: "ASC",
    sortEntity: "TITLE",
  },
  {
    key: "CLASS_NAME_ZA",
    label: "common:class_name_za",
    direction: "DESC",
    sortEntity: "TITLE",
  },
];

const learningCourseSortOptions = [
  {
    key: "COURSE_NAME_AZ",
    label: "common:teacher_course_name_az",
    direction: "ASC",
    sortEntity: "TITLE",
  },
  {
    key: "COURSE_NAME_ZA",
    label: "common:teacher_course_name_za",
    direction: "DESC",
    sortEntity: "TITLE",
  },
];

const blueprintCourseSortOptions = [
  {
    key: "COURSE_NAME_AZ",
    label: "common:blueprint_course_name_az",
    direction: "ASC",
    sortEntity: "TITLE",
  },
  {
    key: "COURSE_NAME_ZA",
    label: "common:blueprint_course_name_za",
    direction: "DESC",
    sortEntity: "TITLE",
  },
];

const sortByOptionsArray = [
  {
    key: "FIRSTNAME_AZ",
    label: "common:first_name_az",
    sortEntity: "FIRST_NAME",
    direction: "ASC",
  },
  {
    key: "FIRSTNAME_ZA",
    label: "common:first_name_za",
    sortEntity: "FIRST_NAME",
    direction: "DESC",
  },
  {
    key: "LASTNAME_AZ",
    label: "common:last_name_az",
    sortEntity: "LAST_NAME",
    direction: "ASC",
  },
  {
    key: "LASTNAME_ZA",
    label: "common:last_name_za",
    sortEntity: "LAST_NAME",
    direction: "DESC",
  },
  {
    key: "MIDDLENAME_AZ",
    label: "common:middle_name_az",
    sortEntity: "MIDDLE_NAME",
    direction: "ASC",
  },
  {
    key: "MIDDLENAME_ZA",
    label: "common:middle_name_za",
    sortEntity: "MIDDLE_NAME",
    direction: "DESC",
  },
  {
    key: "FULLNAME_AZ",
    label: "common:full_name_az",
    sortEntity: "FULL_NAME",
    direction: "ASC",
  },
  {
    key: "FULLNAME_ZA",
    label: "common:full_name_za",
    sortEntity: "FULL_NAME",
    direction: "DESC",
  },
];

export const getNextRoute = key => {
  switch (key) {
    case "SYNC":
      return syncModuleRoutes.jobs;
    case "TERM_MAPPING":
      return syncModuleRoutes.termMapping;
    case "GRADES_MAPPING":
      return syncModuleRoutes.gradesMapping;
    case "SUBJECT_MAPPING":
      return syncModuleRoutes.subjectMapping;
    case "MANAGE_CREDENTIALS":
      return syncModuleRoutes.manageCredentials;
    case "EXCLUDE_TODDLE_DATA":
      return syncModuleRoutes.excludeToddleData;
    case "EXCLUDE_SIS_DATA":
      return syncModuleRoutes.excludeSisData;
    case "MAP_EXISTING_DATA":
      return syncModuleRoutes.mapExistingData;
    case "SYNC_SETTINGS":
      return syncModuleRoutes.syncSettings;
    case "STUDENT_ALERT_TYPE_MAPPING":
      return syncModuleRoutes.studentAlertTypeMapping;
    case "ROLES_PERMISSIONS_MAPPING":
      return syncModuleRoutes.rolesPermissionsMapping;

    default:
      break;
  }
};

export const tabStyle = {
  tabContainerStyle: {
    padding: "12px 12px 12px 12px",
  },
};

export const subTextStyle = {
  fontSize: "1.6rem",
};

export const FEED_STYLE = {
  overflowX: "auto",
  flex: 1,
  borderRadius: "8px",
  border: `1px solid ${colors.borderSubtle}`,
};

export const avatarStyles = { randomizeColor: true, shade: "light" };

export const getSidebar = getMemoizedLangFunction(
  ({
    curriculum,
    t,
    isGradesMappingSaved,
    service,
    serviceName,
    curriculumProgramId,
    syncRosterFromEdval,
  }) => {
    const nomenclatureConstants = getStudentAlertsNomenclature({ t });

    const title = {
      MAPPING_SUB_HEADER: t("integration:mapping_header"),
      FILTERS_SUB_HEADER: t("integration:filters_header"),
      SYNC_SUB_HEADER: t("integration:sync"),
      SYNC: t("common:dashboard"),
      CONFIGURATION_SUB_HEADER: t("common:settings"),
      GRADES_MAPPING: t("integration:manage_grade_mapping"),
      SUBJECT_MAPPING: t("integration:manage_subject_mapping"),
      MANAGE_CREDENTIALS: t("integration:manage_credentials"),
      EXCLUDE_TODDLE_DATA: t("integration:exclude_toddle_data"),
      EXCLUDE_SIS_DATA: t("integration:exclude_sis_data_with_service", {
        service: serviceName,
      }),
      SYNC_SETTINGS: t("common:settings"),
      MAP_EXISTING_DATA: t("integration:map_existing_data"),
      TERM_MAPPING: t("integration:grading_period_mapping"),
      STUDENT_ALERT_TYPE_MAPPING: t(
        "integration:student_alert_type_mapping_with_label",
        {
          ...nomenclatureConstants,
        }
      ),
      ROLES_PERMISSIONS_MAPPING: t("integration:roles_mapping"),
    };

    const isMsaosRosterFromEdval = service === "MSAOS" && syncRosterFromEdval;

    const showManageSubjectMapping =
      ACLStore.can("FeatureFlag:SyncModuleShowManageSubjectMapping") &&
      (getIsLearningCoursesEnabled({
        curriculumProgramId,
      }) ||
        curriculum !== "IB_PYP" ||
        (ACLStore.can("AdminPortal:GradebookSync") &&
          ACLStore.can("FeatureFlag:ShowSameSubjectGradeTasksInGradebook") &&
          _.includes(["COMPASS", "SENTRAL", "TASS"], service)));

    const showManageExistingDataMapping = ACLStore.can(
      "FeatureFlag:SyncModuleShowMapExistingData"
    );

    const showSyncSettings = ACLStore.can(
      "FeatureFlag:SyncModuleShowSyncSettings"
    );

    const showExcludeSisData = ACLStore.can(
      "FeatureFlag:SyncModuleShowExcludeSisData"
    );

    const showExcludeToddleData = ACLStore.can(
      "FeatureFlag:SyncModuleShowExcludeToddleData"
    );
    const showManageGradeMapping = ACLStore.can(
      "FeatureFlag:SyncModuleShowManageGradeMapping"
    );

    const showManageTermMapping =
      !isMsaosRosterFromEdval &&
      getIsLearningCoursesEnabled({
        curriculumProgramId,
      });

    const showStudentAlertTypeMapping = isStudentAlertTypeMappingFeatureEnabled();

    const showRolesPermissionsMapping = isRnpMappingFeatureEnabled();

    const showFiltersHeader = showExcludeSisData || showExcludeToddleData;

    const showMappingHeader =
      showManageGradeMapping ||
      showManageSubjectMapping ||
      showManageExistingDataMapping ||
      showStudentAlertTypeMapping ||
      showRolesPermissionsMapping;

    const showGeneralConfigHeader = showSyncSettings;

    const getRootChildren = () => {
      const children = ["SYNC_HEADER"];

      if (showMappingHeader) {
        children.push("MAPPING_HEADER");
      }

      if (showFiltersHeader) {
        children.push("FILTERS_HEADER");
      }
      if (showGeneralConfigHeader) {
        children.push("CONFIGURATION_HEADER");
      }
      return children;
    };

    const level3Children = {
      SYNC_HEADER: ["SYNC_SUB_HEADER"],
      MAPPING_HEADER: ["MAPPING_SUB_HEADER"],
      FILTERS_HEADER: ["FILTERS_SUB_HEADER"],
    };

    const getLevel3 = () => {
      const rootChildren = getRootChildren();
      return _.map(rootChildren, key => {
        return {
          key: key,
          children: level3Children[key],
          parent: "root",
          tier: 3,
        };
      });
    };

    const getLevel2Children = key => {
      switch (key) {
        case "MAPPING_SUB_HEADER": {
          const children = [];
          if (showManageGradeMapping) {
            children.push("GRADES_MAPPING");
          }
          if (showManageTermMapping) {
            children.push("TERM_MAPPING");
          }
          if (showManageSubjectMapping) {
            children.push("SUBJECT_MAPPING");
          }
          if (showManageExistingDataMapping) {
            children.push("MAP_EXISTING_DATA");
          }
          if (showStudentAlertTypeMapping) {
            children.push("STUDENT_ALERT_TYPE_MAPPING");
          }
          if (showRolesPermissionsMapping) {
            children.push("ROLES_PERMISSIONS_MAPPING");
          }
          return children;
        }

        case "SYNC_SUB_HEADER": {
          const children = [];

          children.push("SYNC");

          if (showSyncSettings) {
            children.push("SYNC_SETTINGS");
          }
          return children;
        }

        case "FILTERS_SUB_HEADER": {
          const children = [];
          if (showExcludeToddleData) {
            children.push("EXCLUDE_TODDLE_DATA");
          }
          if (showExcludeSisData) {
            children.push("EXCLUDE_SIS_DATA");
          }
          return children;
        }
        default:
          break;
      }
    };

    const getLevel2 = () => {
      const level2 = [];
      _.forEach(level3Children, (value, key) => {
        _.forEach(value, child => {
          level2.push({
            key: child,
            children: getLevel2Children(child),
            parent: key,
            tier: 2,
            title: title[child],
          });
        });
      });
      return level2;
    };

    const getLevel1 = () => {
      const level1 = [];
      const level2 = getLevel2();
      _.forEach(level2, value => {
        _.forEach(value.children, child => {
          level1.push({
            key: child,
            children: [],
            parent: value.key,
            tier: 1,
            title: title[child],
            isDisabled:
              !isGradesMappingSaved &&
              !_.includes(["GRADES_MAPPING", "SYNC_SETTINGS"], child),
          });
        });
      });
      return level1;
    };

    return [
      {
        children: getRootChildren(),
        key: "root",
      },
      ...getLevel1(),
      ...getLevel2(),
      ...getLevel3(),
    ];
  }
);

export const getSubjectMappingFeedColumns = ({ t, service }) => {
  const MAPPING_FEED_COLUMNS = [
    {
      id: "tpsSubjectName",
      type: "TEXTCOLUMN",
      label: t("integration:sis_subjects_with_service_plural", { service }),
      style: { width: "50%" },
    },
    {
      id: "toddleSubject",
      type: "CUSTOM",
      label: t("integration:toddle_subjects_plural"),
      style: { width: "50%" },
    },
  ];

  return MAPPING_FEED_COLUMNS;
};

export const compareObjects = (obj1, obj2) => {
  let len = null;
  //check if they're of the same length
  if (_.size(_.keys(obj1)) != _.size(_.keys(obj2))) {
    return false;
  } else {
    len = _.size(_.keys(obj1)); //use any length
  }

  let match = 0; //store number of matched properties
  _.forEach(_.keys(obj1), key => {
    //check if values with the same keys are equal
    if (_.isEqual(_.sortBy(obj1[key]), _.sortBy(obj2[key]))) {
      match++; //increment the variable
    }
  });
  //check if length equals the number of matched properties
  if (_.isEqual(match, len)) {
    return true;
  }

  return false;
};

const isBlueprintCourseEnabled = (metadata, curriculumProgramId) => {
  for (const curriculum of Object.values(metadata)) {
    if (curriculum.curriculumProgramId === curriculumProgramId) {
      return curriculum.settings?.enableBlueprintCourseSync === true;
    }
  }
  return false;
};

const isShowProfilePictureSyncEnabled = (
  metadata = {},
  curriculumProgramId
) => {
  const curriculum = _.find(
    metadata,
    ({ curriculumProgramId: id }) => id === curriculumProgramId
  );

  return _.get(curriculum, "settings.showProfilePictureSyncToggle") === true;
};

const isUseCurrentDateAsFromDateEnabled = (
  metadata = {},
  curriculumProgramId
) => {
  const curriculum = _.find(
    metadata,
    ({ curriculumProgramId: id }) => id === curriculumProgramId
  );

  return _.get(curriculum, "settings.useCurrentDateAsFromDate") === true;
};

/**
 * Determines whether course-related syncing/editing is enabled for a curriculum
 * based on sync service metadata and platform capabilities.
 *
 * Behavior:
 * - If Learning Courses are NOT enabled for the curriculum (Toddle 2.0 orgs),
 *   both academic course code sync and blueprint course sync are disabled.
 *
 * - For blueprint courses (`type === "blueprint"`), this checks the
 *   `enableBlueprintCourseSync` setting from sync metadata.
 *
 * - For regular (academic) courses, course code sync/editing is ALWAYS enabled
 *   for Toddle 2.0 orgs due to recent platform changes (no longer gated by
 *   `isAcademicCourseEnabled`).
 *
 * This helper centralizes course-related enablement logic to ensure consistent
 * behavior across roster and sync flows.
 *
 * @param {string} curriculumId - Curriculum program ID
 * @param {string} [type] - Course type; use `"blueprint"` for blueprint courses
 *
 * @returns {boolean} Whether the requested course type is enabled
 */
export const getIsAcademicCoursesEnabled = (curriculumId, type = "") => {
  const orgId = getUserInfo().org_id;

  const services = getAvailableServicesFromCache({
    variables: {
      id: orgId,
      filters: {
        curriculumId: curriculumId,
      },
    },
  });
  const availableService = _.first(
    _.get(services, "availableSyncServicesV2", [])
  );

  // for toddle 2.0 orgs both course code and Blueprint course sync is not enabled, hence return false
  if (!getIsLearningCoursesEnabled({ curriculumProgramId: curriculumId })) {
    return false;
  }

  if (type === "blueprint") {
    return isBlueprintCourseEnabled(
      _.get(availableService, "metadata", {}),
      curriculumId
    );
  }

  // we are enabling course code sync for all toddle 2.0 orgs by default
  return true;
};

const SYNC_SETTINGS_HANDLERS = {
  showProfilePictureSync: isShowProfilePictureSyncEnabled,
  useCurrentDateAsFromDate: isUseCurrentDateAsFromDateEnabled,
  // future examples:
  // roster: isRosterCourseEnabled,
};

/**
 * Retrieves UI-related settings flags from sync service metadata for a given curriculum.
 *
 * This helper supports fetching one or multiple setting types (flags) in a single call,
 * making it efficient and scalable as more UI-driven settings are introduced.
 *
 * Each setting type is resolved via a corresponding handler defined in
 * `SYNC_SETTINGS_HANDLERS`, which encapsulates the logic for reading that flag
 * from the service metadata.
 *
 * @param {Object} params
 * @param {string} params.curriculumId - Curriculum program ID for which settings are required
 * @param {string|string[]} params.types - One or more setting types to evaluate
 *
 * @returns {Object} An object mapping each requested type to its resolved boolean value
 */
export const getSyncSettingsValue = ({ curriculumId, types = [] }) => {
  const orgId = getUserInfo().org_id;

  const services = getAvailableServicesFromCache({
    variables: {
      id: orgId,
      filters: { curriculumId },
    },
  });

  const availableService = _.first(
    _.get(services, "availableSyncServicesV2", [])
  );

  const metadata = _.get(availableService, "metadata", {});

  // Normalize to array (supports single type as string)
  const resolvedTypes = _.castArray(types);

  return _.reduce(
    resolvedTypes,
    (result, type) => {
      const handler = SYNC_SETTINGS_HANDLERS[type];

      if (_.isFunction(handler)) {
        result[type] = handler(metadata, curriculumId);
      }

      return result;
    },
    {}
  );
};

export const getToddleExclusionTabOptions = ({ t, service, curriculumId }) => {
  return [
    ...(getIsAcademicCoursesEnabled(curriculumId, "blueprint")
      ? [
          {
            label: t("common:blueprint_course_plural"),
            value: "excludeToddleBlueprintCourses",
          },
        ]
      : []),
    ...(getIsLearningCoursesEnabled({ curriculumProgramId: curriculumId })
      ? [
          {
            label: t("common:teacher_course_plural"),
            value: "excludeToddleLearningCourses",
          },
        ]
      : []),
    {
      label: t("common:class_plural"),
      value: "excludeToddleCourses",
    },
    {
      label: t("common:student_plural"),
      value: "excludeToddleStudents",
    },
    {
      label: t("common:staff"),
      value: "excludeToddleStaff",
    },
    ...(showFamilyMemberTab({ service })
      ? [
          {
            label: t("common:family_member_plural"),
            value: "excludeToddleParent",
          },
        ]
      : []),
  ];
};

export const getToddleExclusionColumn = ({ t, type }) => {
  switch (type) {
    case "excludeToddleAcademicCourses": {
      return [
        {
          label: t("integration:sync_label_with_name", {
            label: t("integration:course_code"),
          }),
          id: "name",
          type: "NAMEWITHIMAGE",
          entity: "TITLE",
          key: "toddleData",
          sortType: "dropdown",
          hasSortingEnabled: true,
          sortByOptions: learningCourseSortOptions,
          style: {
            width: "60%",
          },
        },
        {
          locale: "integration:code",
          id: "UID",
          type: "TEXTCOLUMN",
        },
        {
          id: "exclude",
          locale: "common:remove",
          type: "CUSTOM",
          alignment: "center",
          style: {
            width: "10%",
          },
        },
      ];
    }
    case "excludeToddleBlueprintCourses":
    case "excludeToddleLearningCourses": {
      return [
        {
          label: t("integration:sync_label_with_name", {
            label:
              type === "excludeToddleLearningCourses"
                ? t("common:teacher_course")
                : t("course:blueprint_course_lowercase"),
          }),
          id: "name",
          type: "NAMEWITHIMAGE",
          entity: "TITLE",
          key: "toddleData",
          sortType: "dropdown",
          hasSortingEnabled: true,
          sortByOptions:
            type === "excludeToddleLearningCourses"
              ? learningCourseSortOptions
              : blueprintCourseSortOptions,
          style: {
            width: "60%",
          },
        },
        {
          locale: "integration:uid",
          id: "UID",
          type: "TEXTCOLUMN",
        },
        {
          id: "exclude",
          locale: "common:remove",
          type: "CUSTOM",
          alignment: "center",
          style: {
            width: "10%",
          },
        },
      ];
    }
    case "excludeToddleCourses": {
      return [
        {
          label: t("integration:sync_label_with_name", {
            label: t("common:class"),
          }),
          id: "name",
          type: "NAMEWITHIMAGE",
          entity: "TITLE",
          key: "toddleData",
          sortType: "dropdown",
          hasSortingEnabled: true,
          sortByOptions: courseSortOptions,
          style: {
            width: "60%",
          },
        },
        {
          locale: "integration:uid",
          id: "UID",
          type: "TEXTCOLUMN",
        },
        {
          id: "exclude",
          locale: "common:remove",
          type: "CUSTOM",
          alignment: "center",
          style: {
            width: "10%",
          },
        },
      ];
    }
    case "excludeToddleStaff": {
      return [
        {
          locale: "common:name",
          id: "name",
          type: "NAMEWITHIMAGE",
          entity: "NAME",
          key: "toddleData",
          sortType: "dropdown",
          hasSortingEnabled: true,
          sortByOptions: getUserSortOptions({
            sortOptions: sortByOptionsArray,
            keyToMap: "sortEntity",
            type: "staff",
          }),
          style: {
            width: "60%",
          },
        },
        {
          locale: "common:uid",
          id: "id",
          type: "TEXTCOLUMN",
        },
        {
          id: "exclude",
          locale: "common:remove",
          type: "CUSTOM",
          alignment: "center",
          style: {
            width: "10%",
          },
        },
      ];
    }
    case "excludeToddleParent": {
      return [
        {
          locale: "common:name",
          id: "title",
          type: "NAMEWITHIMAGE",
          style: {
            width: "60%",
          },
        },
        {
          locale: "common:uid",
          id: "id",
          type: "TEXTCOLUMN",
        },
        {
          id: "exclude",
          type: "CUSTOM",
          locale: "common:remove",
          alignment: "center",
          style: {
            width: "10%",
          },
        },
      ];
    }
    case "excludeToddleStudents": {
      return [
        {
          locale: "common:name",
          id: "name",
          type: "NAMEWITHIMAGE",
          entity: "NAME",
          key: "toddleData",
          sortType: "dropdown",
          hasSortingEnabled: true,
          sortByOptions: getUserSortOptions({
            sortOptions: sortByOptionsArray,
            keyToMap: "sortEntity",
            type: "student",
          }),
          style: {
            width: "60%",
          },
        },
        {
          locale: "common:uid",
          id: "uid",
          type: "TEXTCOLUMN",
        },
        {
          id: "exclude",
          locale: "common:remove",
          type: "CUSTOM",
          alignment: "center",
          style: {
            width: "10%",
          },
        },
      ];
    }

    default:
      break;
  }
};

export const getSisExclusionColumns = ({ t, type }) => {
  switch (type) {
    case "BLUEPRINT_COURSE":
    case "ACADEMIC_COURSE":
    case "COURSE":
    case "LEARNING_COURSE": {
      return [
        {
          locale: "common:title",
          id: "title",
          type: "NAMEWITHIMAGE",
          style: {
            width: "60%",
          },
        },
        {
          locale: "integration:uid",
          id: "UID",
          type: "TEXTCOLUMN",
          alignment: "left",
        },
        {
          id: "remove",
          type: "CUSTOM",
          locale: "common:remove",
          alignment: "center",
          style: {
            width: "10%",
          },
        },
      ];
    }

    case "STAFF": {
      return [
        {
          locale: "common:name",
          id: "name",
          type: "NAMEWITHIMAGE",
          style: {
            width: "60%",
          },
        },
        {
          locale: "integration:uid",
          id: "UID",
          type: "TEXTCOLUMN",
        },
        {
          id: "remove",
          type: "CUSTOM",
          locale: "common:remove",
          alignment: "center",
          style: {
            width: "10%",
          },
        },
      ];
    }

    case "PARENT": {
      return [
        {
          locale: "common:name",
          id: "title",
          type: "NAMEWITHIMAGE",
          style: {
            width: "60%",
          },
        },
        {
          locale: "integration:uid",
          id: "UID",
          type: "TEXTCOLUMN",
        },
        {
          id: "remove",
          type: "CUSTOM",
          locale: "common:remove",
          alignment: "center",
          style: {
            width: "10%",
          },
        },
      ];
    }

    case "STUDENT": {
      return [
        {
          locale: "common:name",
          id: "name",
          type: "NAMEWITHIMAGE",
          style: {
            width: "60%",
          },
        },
        {
          locale: "integration:uid",
          id: "UID",
          type: "TEXTCOLUMN",
        },
        {
          id: "remove",
          type: "CUSTOM",
          locale: "common:remove",
          alignment: "center",
          style: {
            width: "10%",
          },
        },
      ];
    }

    default:
      break;
  }
};
export const getToddleExclusionFeedData = ({
  currentValue,
  excludedIds,
  activeTab,
}) => {
  switch (activeTab) {
    case "excludeToddleStudents": {
      const feedData = _.map(currentValue.edges, (obj, key) => {
        const {
          email,
          firstName,
          lastName,
          middleName,
          prefix,
          suffix,
          pronouns,
          preferredName,
          profileImage,
          id,
          alertTypes = [],
        } = _.get(obj, "node", {});

        const name = getFormattedUserName({
          user: {
            firstName,
            lastName,
            middleName,
            prefix,
            suffix,
            pronouns,
            preferredName,
          },
          type: "student",
        });

        return {
          id,
          name: {
            id,
            name,
            profileImage,
            email,
            studentAlertsConfig: {
              alertTypes,
              studentId: id,
            },
          },
          exclude: {
            UId: id,
            name,
            isChecked: _.indexOf(excludedIds, `TD-${id}`) !== -1,
          },
          uid: id,
        };
      });
      return feedData;
    }
    case "excludeToddleAcademicCourses": {
      const excludedData = _.filter(
        currentValue,
        ({ code }) => _.indexOf(excludedIds, `TD-${code}`) !== -1
      );

      const feedData = _.map(excludedData, (obj, key) => {
        const UId = _.get(obj, `code`, "");
        const name = _.get(obj, `label`, "");

        return {
          id: UId,
          UID: UId,
          name: { name: name, id: UId },
          exclude: {
            UId,
            name,
            isChecked: true,
          },
        };
      });

      return feedData;
    }
    case "excludeToddleBlueprintCourses":
    case "excludeToddleLearningCourses": {
      const feedData = _.map(currentValue, (obj, key) => {
        const UId = _.get(obj, `node.id`, "");
        const name = _.get(obj, `node.title`, "");

        return {
          id: UId,
          UID: UId,
          name: { name: name, id: UId },
          exclude: {
            UId,
            name,
            isChecked: _.indexOf(excludedIds, `TD-${UId}`) !== -1,
          },
        };
      });

      return feedData;
    }
    case "excludeToddleCourses": {
      const feedData = _.map(currentValue, (obj, key) => {
        const UId = _.get(obj, `node.id`, "");
        const name = _.get(obj, `node.title`, "");

        return {
          id: UId,
          UID: UId,
          name: { name: name, id: UId },
          exclude: {
            UId,
            name,
            isChecked: _.indexOf(excludedIds, `TD-${UId}`) !== -1,
          },
        };
      });

      return feedData;
    }
    case "excludeToddleParent": {
      const feedData = _.map(currentValue, familyMember => {
        const id = _.get(familyMember, "id", "");
        const email = _.get(familyMember, "email", "");

        const {
          firstName,
          lastName,
          middleName,
          preferredName,
          prefix,
          suffix,
          pronouns,
        } = familyMember;

        const name = getFormattedUserName({
          user: {
            firstName,
            lastName,
            middleName,
            preferredName,
            prefix,
            suffix,
            pronouns,
          },
          type: "parent",
        });

        return {
          id: id,
          title: {
            email,
            name,
            id: id,
          },
          exclude: {
            UId: id,
            name,
            isChecked: _.indexOf(excludedIds, `TD-${id}`) !== -1,
          },
        };
      });
      return feedData;
    }
    case "excludeToddleStaff": {
      const feedData = _.map(currentValue, (obj, key) => {
        const id = _.get(obj, `node.id`, "");
        const {
          firstName,
          lastName,
          middleName,
          preferredName,
          prefix,
          suffix,
          pronouns,
        } = _.get(obj, "node", {});

        const name = getFormattedUserName({
          user: {
            firstName,
            lastName,
            middleName,
            preferredName,
            prefix,
            suffix,
            pronouns,
          },
          type: "staff",
        });

        const email = _.get(obj, `node.email`, "");
        return {
          id: id,
          name: {
            id: id,
            name,
            email: email,
          },
          exclude: {
            id,
            name,
            isChecked: _.indexOf(excludedIds, `TD-${id}`) !== -1,
          },
        };
      });

      return feedData;
    }
    default:
      break;
  }
};

export const getSisExclusionTabOptions = ({ t, service, curriculumId }) => {
  const TAB_OPTIONS = [
    // ...(getIsAcademicCoursesEnabled(curriculumId)
    //   ? [
    //       {
    //         label: t("integration:course_code_plural"),
    //         value: "CourseCodes",
    //       },
    //     ]
    //   : []),

    ...(getIsAcademicCoursesEnabled(curriculumId, "blueprint")
      ? [
          {
            label: t("common:blueprint_course_plural"),
            value: "BlueprintCourses",
          },
        ]
      : []),

    ...(getIsLearningCoursesEnabled({ curriculumProgramId: curriculumId })
      ? [
          {
            label: t("common:teacher_course_plural"),
            value: "Courses",
          },
        ]
      : []),

    {
      label: t("common:class_plural"),
      value: "Classes",
    },
    {
      label: t("common:student_plural"),
      value: "Students",
    },
    {
      label: t("common:staff"),
      value: "Staff",
    },
    ...(showFamilyMemberTab({ service })
      ? [
          {
            label: t("common:family_member_plural"),
            value: "Parents",
          },
        ]
      : []),
  ];

  return TAB_OPTIONS;
};

export const getLabelText = ({ activeTab, t }) => {
  switch (activeTab) {
    case "CourseCodes":
      return t("integration:course_code_plural");
    case "BlueprintCourses":
      return t("common:blueprint_course_plural");
    case "Courses":
      return t("common:teacher_course_plural");
    case "Classes":
      return t("common:class_plural");
    case "Students":
      return t("common:student_plural");
    case "Parents":
      return t("common:family_member_plural");
    case "Staff":
      return t("common:staff");
    default:
      return "";
  }
};
export const getTypeAndSubType = ({ activeTab }) => {
  switch (activeTab) {
    case "CourseCodes":
    case "excludeToddleAcademicCourses":
      return {
        type: "ACADEMIC_COURSE",
      };
    case "BlueprintCourses":
    case "excludeToddleBlueprintCourses":
      return {
        type: "BLUEPRINT_COURSE",
      };
    case "Courses":
    case "excludeToddleLearningCourses":
      return {
        type: "LEARNING_COURSE", // Add LEARNING_COURSE
      };
    case "Classes":
    case "excludeToddleCourses":
      return {
        type: "COURSE",
      };
    case "Students":
    case "excludeToddleStudents":
      return {
        type: "USER",
        subType: "STUDENT",
      };
    case "Parents":
    case "excludeToddleParent":
      return {
        type: "PARENT",
      };
    case "Staff":
    case "excludeToddleStaff":
      return {
        type: "USER",
        subType: "STAFF",
      };
    default:
      return [];
  }
};

export const getFieldKey = ({ activeTab }) => {
  switch (activeTab) {
    case "CourseCodes":
      return "excludeTPSAcademicCourses";
    case "BlueprintCourses":
      return "excludeTPSBlueprintCourses";
    case "Courses":
      return "excludeTPSLearningCourses";
    case "Classes":
      return "excludeTPSCourses";
    case "Students":
      return "excludeTPSStudents";
    case "Parents":
      return "excludeTPSParent";
    case "Staff":
      return "excludeTPSStaff";
    default:
      return [];
  }
};

export const getButtonText = ({ activeTab, t }) => {
  switch (activeTab) {
    case "CourseCodes":
      return t("common:add_with_label", {
        label: t("integration:course_code_plural"),
      });
    case "BlueprintCourses":
      return t("common:add_with_label", {
        label: t("common:blueprint_course_plural"),
      });
    case "Courses":
      return t("common:add_with_label", {
        label: t("common:teacher_course_plural"),
      });
    case "Classes":
      return t("common:add_with_label", {
        label: t("common:classes"),
      });
    case "Students":
      return t("common:add_with_label", {
        label: t("common:students"),
      });
    case "Parents":
      return t("common:add_with_label", {
        label: t("common:family_member_plural"),
      });
    case "Staff":
      return t("common:add_with_label", {
        label: t("common:staff"),
      });
    default:
      return [];
  }
};

export const getSisExclusionListFeedData = ({ subTasks, type }) => {
  switch (type) {
    case "ACADEMIC_COURSE":
    case "BLUEPRINT_COURSE":
    case "LEARNING_COURSE": {
      const feedData = _.map(subTasks, ({ node }) => {
        const {
          id,
          parsedData: { serviceId, title, label, classMapping, classes } = {},
        } = node;
        return {
          id,
          title: { name: title || label, id },
          UID: serviceId,
          remove: {
            serviceId,
            name: title || label,
            classMapping: classMapping || classes || [],
          },
        };
      });
      return feedData;
    }
    case "COURSE": {
      const feedData = _.map(subTasks, ({ node }) => {
        const { id, parsedData: { serviceId, title } = {} } = node;
        return {
          id,
          title: { name: title, id },
          UID: serviceId,
          remove: {
            serviceId,
            name: title,
          },
        };
      });
      return feedData;
    }

    case "PARENT": {
      const feedData = _.map(subTasks, ({ node }) => {
        const {
          id,
          parsedData: {
            serviceId,
            firstName,
            lastName,
            middleName,
            preferredName,
            prefix,
            suffix,
            pronouns,
            email,
          } = {},
        } = node;

        const name = getFormattedUserName({
          user: {
            firstName,
            lastName,
            middleName,
            preferredName,
            prefix,
            suffix,
            pronouns,
          },
          type: "parent",
        });

        return {
          id,
          title: { name, id, email },
          UID: String(serviceId),
          remove: {
            serviceId: String(serviceId),
            name,
          },
        };
      });
      return feedData;
    }
    case "STUDENT": {
      const feedData = _.map(subTasks, ({ node }) => {
        const {
          id,
          parsedData: {
            serviceId,
            firstName,
            lastName,
            middleName,
            prefix,
            suffix,
            pronouns,
            preferredName,
            email,
          } = {},
        } = node;

        const name = getFormattedUserName({
          user: {
            firstName,
            lastName,
            middleName,
            prefix,
            suffix,
            pronouns,
            preferredName,
          },
          type: "student",
        });

        return {
          id,
          name: { name, id, email },
          UID: serviceId,
          remove: {
            name,
            serviceId,
          },
        };
      });
      return feedData;
    }
    case "STAFF": {
      const feedData = _.map(subTasks, ({ node }) => {
        const {
          id,
          parsedData: {
            serviceId,
            firstName,
            lastName,
            middleName,
            preferredName,
            prefix,
            suffix,
            pronouns,
            email,
          } = {},
        } = node;

        const name = getFormattedUserName({
          user: {
            firstName,
            lastName,
            middleName,
            preferredName,
            prefix,
            suffix,
            pronouns,
          },
          type: "staff",
        });

        return {
          id,
          name: { name, id, email },
          UID: serviceId,
          remove: {
            serviceId,
            name,
          },
        };
      });
      return feedData;
    }

    default:
      break;
  }
};

export const getSisAddModalFeedData = ({ subTasks, type, excludedRowIds }) => {
  switch (type) {
    case "BLUEPRINT_COURSE":
    case "ACADEMIC_COURSE":
    case "LEARNING_COURSE": {
      const feedData = _.map(subTasks, ({ node }) => {
        const {
          id,
          parsedData: { serviceId, title, label, classMapping = [] } = {},
        } = node;
        return {
          name: title || label,
          id: serviceId,
          isChecked: _.includes(excludedRowIds, serviceId),
          classes: _.map(classMapping, classItem => ({
            id: classItem.sisClassServiceId,
            title: classItem.sisClassTitle,
            uid: classItem.sisClassUid,
          })),
        };
      });
      return feedData;
    }
    case "COURSE": {
      const feedData = _.map(subTasks, ({ node }) => {
        const { id, parsedData: { serviceId, title } = {} } = node;
        return {
          name: title,
          id: serviceId,
          isChecked: _.includes(excludedRowIds, serviceId),
        };
      });
      return feedData;
    }

    case "PARENT": {
      const feedData = _.map(subTasks, ({ node }) => {
        const {
          id,
          parsedData: {
            serviceId,
            firstName,
            lastName,
            middleName,
            preferredName,
            prefix,
            suffix,
            pronouns,
            email,
          } = {},
        } = node;

        return {
          name: getFormattedUserName({
            user: {
              firstName,
              lastName,
              middleName,
              preferredName,
              prefix,
              suffix,
              pronouns,
            },
            type: "parent",
          }),
          email,
          id: serviceId,
          isChecked: _.includes(excludedRowIds, String(serviceId)),
        };
      });
      return feedData;
    }
    case "STUDENT": {
      const feedData = _.map(subTasks, ({ node }) => {
        const {
          id,
          parsedData: {
            serviceId,
            firstName,
            lastName,
            middleName,
            prefix,
            suffix,
            pronouns,
            preferredName,
            email,
          } = {},
        } = node;

        return {
          name: getFormattedUserName({
            user: {
              firstName,
              lastName,
              middleName,
              prefix,
              suffix,
              pronouns,
              preferredName,
            },
            type: "student",
          }),
          email,
          id: serviceId,
          isChecked: _.includes(excludedRowIds, serviceId),
        };
      });
      return feedData;
    }
    case "STAFF": {
      const feedData = _.map(subTasks, ({ node }) => {
        const {
          id,
          parsedData: {
            serviceId,
            firstName,
            lastName,
            middleName,
            preferredName,
            prefix,
            suffix,
            pronouns,
            email,
          } = {},
        } = node;

        return {
          name: getFormattedUserName({
            user: {
              firstName,
              lastName,
              middleName,
              preferredName,
              prefix,
              suffix,
              pronouns,
            },
            type: "staff",
          }),
          email,
          id: serviceId,
          isChecked: _.includes(excludedRowIds, serviceId),
        };
      });
      return feedData;
    }

    default:
      break;
  }
};

export const getCurrentButtonText = ({ activeTab, t }) => {
  switch (activeTab) {
    case "excludeToddleAcademicCourses":
      return t("common:add_with_label", {
        label: t("integration:course_code_plural"),
      });
    case "excludeToddleBlueprintCourses":
      return t("common:add_with_label", {
        label: t("common:blueprint_course_plural"),
      });
    case "excludeToddleLearningCourses":
      return t("common:add_teacher_course_plural");
    case "excludeToddleCourses":
      return t("common:add_class_plural");
    case "excludeToddleStaff":
      return t("common:add_with_label", { label: t("common:staff") });
    case "excludeToddleStudents":
      return t("common:add_with_label", {
        label: t("common:student_plural_lowercase"),
      });
    case "excludeToddleParent":
      return t("common:add_with_label", {
        label: t("common:family_member_plural"),
      });
    default:
      break;
  }
};

export const getToddleExclusionWarningLabel = ({ activeTab, t }) => {
  switch (activeTab) {
    case "excludeToddleBlueprintCourses":
      return t("common:blueprint_course_plural");
    case "excludeToddleLearningCourses":
      return t("common:teacher_course_plural");
    case "excludeToddleCourses":
      return t("common:class_plural");
    case "excludeToddleStaff":
      return t("common:staff");
    case "excludeToddleStudents":
      return t("common:student_plural");
    case "excludeToddleParent":
      return t("common:family_member_plural");
    default:
      break;
  }
};

export const saveExclusionDataAndRefetch = ({
  currentUid,
  removedIds,
  curriculumKey,
  updateExcludedEntitiesCache,
  fieldKey,
  excludedEntitiesQueryVariables,
  changeIntegrationConfig,
  curriculum,
  selectedValue,
  refetchQueries,
  curriculumId,
  t,
}) => {
  const updatedData = _.filter(removedIds, id => id !== `TD-${currentUid}`);

  const isAcronymRequired = !_.includes(
    [
      CURRICULUM_TYPE_MYP,
      CURRICULUM_TYPE_PYP,
      CURRICULUM_TYPE_UBD,
      CURRICULUM_TYPE_DP,
    ],
    curriculumKey
  );

  updateExcludedEntitiesCache({
    data: selectedValue ? selectedValue : updatedData,
    variables: excludedEntitiesQueryVariables,
  });

  changeIntegrationConfig({
    input: [
      {
        [fieldKey]: selectedValue ? selectedValue : updatedData,
        curriculumType: curriculum,
        ...(isAcronymRequired ? { acronym: curriculumKey } : []),
        curriculumId,
      },
    ],
    successToastMsg: t("toastMsgs:saved_changes_successfully"),
    shouldRefetch: true,
    refetchQueries: refetchQueries,
  });
};

export const getExistingDataMappingTabOptions = ({
  t,
  service,
  curriculumId,
}) => {
  const TAB_OPTIONS = [
    ...(getIsAcademicCoursesEnabled(curriculumId)
      ? [
          {
            label: t("integration:course_code_plural"),
            value: "CourseCode",
          },
        ]
      : []),
    ...(getIsAcademicCoursesEnabled(curriculumId, "blueprint")
      ? [
          {
            label: t("course:blueprint_course_lowercase_plural"),
            value: "BlueprintCourse",
          },
        ]
      : []),
    ...(getIsLearningCoursesEnabled({ curriculumProgramId: curriculumId })
      ? [
          {
            label: t("common:teacher_course_plural"),
            value: "Course",
          },
        ]
      : []),
    {
      label: t("common:class_plural"),
      value: "Class",
    },
    {
      label: t("common:student_plural"),
      value: "Student",
    },
    {
      label: t("common:staff"),
      value: "Staff",
    },
    ...(showFamilyMemberTab({ service })
      ? [
          {
            label: t("common:family_member_plural"),
            value: "Parent",
          },
        ]
      : []),
  ];

  return TAB_OPTIONS;
};

export const getExistingDataMappingFeedColumns = _.memoize(
  ({ type, service, t, isProcessScreen }) => {
    switch (type) {
      case "COURSE_CODE": {
        return [
          {
            locale: "integration:toddle_course_code_id",
            id: "toddleId",
            type: "TEXTCOLUMN",
            isSticky: true,
            style: {
              width: isProcessScreen ? "220px" : "180px",
            },
            entity: "toddleId",
            key: "toddleId",
          },
          {
            locale: "integration:toddle_course_code",
            id: "toddleData",
            type: isProcessScreen ? "TEXTCOLUMN" : "NAMEWITHIMAGE",
            isSticky: true,
            style: !isProcessScreen
              ? {
                  width: "348px",
                }
              : {
                  width: "400px",
                },
            entity: "TITLE",
            key: "toddleData",
            sortType: "dropdown",
            hasSortingEnabled: true,
            sortByOptions: [
              {
                key: "COURSE_NAME_AZ",
                label: "common:course_name_az",
                direction: "ASC",
                sortEntity: "TITLE",
              },
              {
                key: "COURSE_NAME_ZA",
                label: "common:course_name_za",
                direction: "DESC",
                sortEntity: "TITLE",
              },
            ],
          },
          {
            label: t("integration:sis_course_code_with_service", { service }),
            id: "sisData",
            type: "CUSTOM",
            isSticky: true,
          },
          // TODO- Check this for V3 module
          ...(isProcessScreen
            ? [
                {
                  id: "toddleExclusion",
                  type: "CUSTOM",
                  label: t("integration:add_to_exception_list"),
                  alignment: "center",
                  style: {
                    width: `200px`,
                  },
                },
              ]
            : []),
        ];
      }
      case "BLUEPRINT_COURSE":
      case "LEARNING_COURSE": {
        return [
          {
            locale:
              type === "BLUEPRINT_COURSE"
                ? "integration:toddle_blueprint_course_id"
                : "integration:toddle_course_id",
            id: "toddleId",
            type: "TEXTCOLUMN",
            isSticky: true,
            style: {
              width: isProcessScreen ? "220px" : "180px",
            },
            entity: "toddleId",
            key: "toddleId",
          },
          {
            locale:
              type === "BLUEPRINT_COURSE"
                ? "integration:toddle_blueprint_course"
                : "integration:toddle_teacher_course",
            id: "toddleData",
            type: isProcessScreen ? "TEXTCOLUMN" : "NAMEWITHIMAGE",
            isSticky: true,
            style: !isProcessScreen
              ? {
                  width: "348px",
                }
              : {
                  width: "400px",
                },
            entity: "TITLE",
            key: "toddleData",
            sortType: "dropdown",
            hasSortingEnabled: true,
            sortByOptions: [
              {
                key: "COURSE_NAME_AZ",
                label:
                  type === "BLUEPRINT_COURSE"
                    ? "common:blueprint_course_name_az"
                    : "common:teacher_course_name_az",
                direction: "ASC",
                sortEntity: "TITLE",
              },
              {
                key: "COURSE_NAME_ZA",
                label:
                  type === "BLUEPRINT_COURSE"
                    ? "common:blueprint_course_name_za"
                    : "common:teacher_course_name_za",
                direction: "DESC",
                sortEntity: "TITLE",
              },
            ],
          },
          {
            label:
              type === "BLUEPRINT_COURSE"
                ? t("integration:sis_blueprint_course_with_service", {
                    service,
                  })
                : t("integration:sis_course_with_service", { service }),
            id: "sisData",
            type: "CUSTOM",
            isSticky: true,
          },
          // TODO- Check this for V3 module
          ...(isProcessScreen
            ? [
                {
                  id: "toddleExclusion",
                  type: "CUSTOM",
                  label: t("integration:add_to_exception_list"),
                  alignment: "center",
                  style: {
                    width: `200px`,
                  },
                },
              ]
            : []),
        ];
      }
      case "COURSE": {
        return [
          {
            locale: "integration:toddle_class_id",
            id: "toddleId",
            type: "TEXTCOLUMN",
            isSticky: true,
            style: {
              width: isProcessScreen ? "220px" : "180px",
            },
            entity: "toddleId",
            key: "toddleId",
          },
          {
            locale: "integration:toddle_class",
            id: "toddleData",
            type: isProcessScreen ? "TEXTCOLUMN" : "NAMEWITHIMAGE",
            isSticky: true,
            style: !isProcessScreen
              ? {
                  width: "348px",
                }
              : {
                  width: "400px",
                },
            entity: "TITLE",
            key: "toddleData",
            sortType: "dropdown",
            hasSortingEnabled: true,
            sortByOptions: [
              {
                key: "CLASS_NAME_AZ",
                label: "common:class_name_az",
                direction: "ASC",
                sortEntity: "TITLE",
              },
              {
                key: "CLASS_NAME_ZA",
                label: "common:class_name_za",
                direction: "DESC",
                sortEntity: "TITLE",
              },
            ],
          },
          {
            label: t("integration:sis_class_with_service", { service }),
            id: "sisData",
            type: "CUSTOM",
            isSticky: true,
          },
          ...(isProcessScreen
            ? [
                {
                  id: "toddleExclusion",
                  type: "CUSTOM",
                  label: t("integration:add_to_exception_list"),
                  alignment: "center",
                  style: {
                    width: `200px`,
                  },
                },
              ]
            : []),
        ];
      }
      case "STAFF": {
        return [
          {
            locale: "integration:toddle_teacher",
            id: "toddleData",
            type: "NAMEWITHIMAGE",
            isSticky: true,
            style: {
              width: isProcessScreen ? "380px" : "480px",
            },
            entity: "NAME",
            sortType: "dropdown",
            hasSortingEnabled: true,
            sortByOptions: getUserSortOptions({
              sortOptions: sortByOptionsArray,
              keyToMap: "sortEntity",
              type: "staff",
            }),
          },
          ...(isProcessScreen
            ? [
                {
                  id: "UID",
                  type: "TEXTCOLUMN",
                  label: t("common:uid"),
                  style: {
                    width: `220px`,
                  },
                },
              ]
            : []),
          {
            label: t("integration:sis_staff_with_service", { service }),
            id: "sisData",
            type: "CUSTOM",
            isSticky: true,
          },
          ...(isProcessScreen
            ? [
                {
                  id: "toddleExclusion",
                  type: "CUSTOM",
                  label: t("integration:add_to_exception_list"),
                  alignment: "center",
                  style: {
                    width: `200px`,
                  },
                },
              ]
            : []),
        ];
      }
      case "PARENT": {
        return [
          {
            locale: "integration:toddle_parent",
            id: "toddleData",
            type: "NAMEWITHIMAGE",
            isSticky: true,
            style: {
              width: !isProcessScreen ? "380px" : "480px",
            },
          },
          ...(isProcessScreen
            ? [
                {
                  id: "StudentName",
                  type: "TEXTCOLUMN",
                  label: t("integration:family_of_student"),
                  style: {
                    width: `220px`,
                  },
                },
              ]
            : []),
          {
            label: t("integration:sis_parent_with_service", { service }),
            id: "sisData",
            type: "CUSTOM",
            isSticky: true,
          },
          ...(isProcessScreen
            ? [
                {
                  id: "toddleExclusion",
                  type: "CUSTOM",
                  label: t("integration:add_to_exception_list"),
                  alignment: "center",
                  style: {
                    width: `200px`,
                  },
                },
              ]
            : []),
        ];
      }
      case "STUDENT": {
        return [
          {
            locale: "integration:toddle_student",
            id: "toddleData",
            type: "NAMEWITHIMAGE",
            isSticky: true,
            style: {
              width: isProcessScreen ? "320px" : "480px",
            },
            entity: "NAME",
            sortType: "dropdown",
            hasSortingEnabled: true,
            sortByOptions: getUserSortOptions({
              sortOptions: sortByOptionsArray,
              keyToMap: "sortEntity",
              type: "student",
            }),
          },
          ...(isProcessScreen
            ? [
                {
                  id: "UID",
                  type: "TEXTCOLUMN",
                  label: t("common:uid"),
                  style: {
                    width: `142px`,
                  },
                },
                {
                  id: "Grade",
                  type: "TEXTCOLUMN",
                  label: t("common:grade"),
                  style: {
                    width: `100px`,
                  },
                },
                {
                  id: "YearGroup",
                  type: "TEXTCOLUMN",
                  label: t("common:year_group"),
                  style: {
                    width: `142px`,
                  },
                },
              ]
            : []),
          {
            label: t("integration:sis_student_with_service", { service }),
            id: "sisData",
            type: "CUSTOM",
            isSticky: true,
            style: {
              width: isProcessScreen ? "344px" : "",
            },
          },
          ...(isProcessScreen
            ? [
                {
                  id: "toddleExclusion",
                  type: "CUSTOM",
                  label: t("integration:add_to_exception_list"),
                  alignment: "center",
                },
              ]
            : []),
        ];
      }
      default:
        break;
    }
  }
);

export const getActiveTabLabels = ({ activeTab, t }) => {
  switch (activeTab) {
    case "CourseCode":
      return {
        entity: t("integration:course_code_plural"),
        entitySingular: t("integration:course_code"),
      };
    case "BlueprintCourse":
      return {
        entity: t("course:blueprint_course_lowercase_plural"),
        entitySingular: t("course:blueprint_course_lowercase"),
      };
    case "Class":
      return {
        entity: t("common:classes"),
        entitySingular: t("common:class"),
      };
    case "Course":
      return {
        entity: t("common:teacher_course_plural"),
        entitySingular: t("common:teacher_course"),
      };
    case "Staff":
      return {
        entity: t("common:staff"),
        entitySingular: t("common:staff"),
      };
    case "Student":
      return {
        entity: t("common:students"),
        entitySingular: t("common:student"),
      };
    case "Parent":
      return {
        entity: t("common:family_member_plural"),
        entitySingular: t("common:family_member"),
      };
    default:
      break;
  }
};

//get Data mapping feedList
export const getExistingDataMappingFeed = ({
  activeTab,
  subTasks,
  thirdPartyMapping,
  academicCourses,
  courses,
  students,
  staff,
  familyData,
  curriculum,
  isProcessScreen,
  excludedRowIds,
  t,
  curriculumId,
}) => {
  switch (activeTab) {
    case "CourseCode": {
      const feedData = _.map(academicCourses, academicCourse => {
        const id = _.get(academicCourse, "id", "");
        const courseCode = _.get(academicCourse, "code", "");
        const courseCodeLabel = _.get(academicCourse, "label", "");
        const currentMappingList = _.filter(thirdPartyMapping, mapping => {
          return mapping.toddleId === id;
        });

        const thirdPartyIdList = _.map(currentMappingList, currentMapping =>
          _.get(currentMapping, "thirdPartyId", "")
        );
        const sisData = {
          toddleId: id,
          thirdPartyIdList,
          isExcluded: false,
        };
        return {
          id,
          toddleId: courseCode,
          courseCode,
          toddleData: {
            id: id,
            name: courseCodeLabel,
          },
          sisData,
        };
      });
      return feedData;
    }
    case "BlueprintCourse":
    case "Course": {
      const feedData = _.map(courses.edges, course => {
        const id = _.get(course, "node.id", "");

        const sourcedId = _.get(course, "node.sourcedId", "");

        const title = _.get(course, "node.title", "");

        const grade = _.first(_.get(course, "node.grades", []));

        const subjects = _.first(_.get(course, "node.subjects", []));

        const subjectVariants = _.first(_.get(subjects, "variants", []));

        const subjectLevel = _.get(subjectVariants, "level.label", "");

        const gradeName = _.get(grade, "name", "");

        const isArchived = _.get(course, "node.isArchived", false);

        const currentMappingList = _.filter(thirdPartyMapping, mapping => {
          return mapping.toddleId === id;
        });

        const thirdPartyIdList = _.map(currentMappingList, currentMapping =>
          _.get(currentMapping, "thirdPartyId", "")
        );

        const isExcluded = _.includes(excludedRowIds, `TD-${id}`);

        return {
          id,
          toddleId: id,
          toddleData: isProcessScreen
            ? title
            : {
                name: title,
                id: id,
                ...(curriculum === "IB_DP"
                  ? {
                      email: `${t("common:grade")}: ${gradeName}${
                        subjectLevel
                          ? `, ${t("common:subject_level")}: ${subjectLevel}`
                          : ""
                      }`,
                    }
                  : []),
              },
          sisData: { toddleId: id, thirdPartyIdList, isExcluded },
          isArchived,
          toddleExclusion: {
            isExcluded,
            toddleId: `TD-${id}`,
          },
          cellStyles: isProcessScreen
            ? {
                commonCellStyles: {
                  backgroundColor: isExcluded ? colors.surfaceWarning : "",
                },
              }
            : null,
        };
      });

      return feedData;
    }
    case "Class": {
      const feedData = _.map(courses.edges, course => {
        const id = _.get(course, "node.id", "");

        const sourcedId = _.get(course, "node.sourcedId", "");

        const title = _.get(course, "node.title", "");

        const grade = _.first(_.get(course, "node.grades", []));

        const subjects = _.first(_.get(course, "node.subjects", []));

        const subjectVariants = _.first(_.get(subjects, "variants", []));

        const subjectLevel = _.get(subjectVariants, "level.label", "");

        const gradeName = _.get(grade, "name", "");

        const isArchived = _.get(course, "node.isArchived", false);

        const currentMappingList = _.filter(thirdPartyMapping, mapping => {
          return mapping.toddleId === id;
        });

        const thirdPartyIdList = _.map(currentMappingList, currentMapping =>
          _.get(currentMapping, "thirdPartyId", "")
        );

        const isExcluded = _.includes(excludedRowIds, `TD-${id}`);

        return {
          id,
          toddleId: sourcedId,
          toddleData: isProcessScreen
            ? title
            : {
                name: title,
                id: id,
                ...(curriculum === "IB_DP" &&
                !getIsLearningCoursesEnabled({
                  curriculumProgramId: curriculumId,
                })
                  ? {
                      email: `${t("common:grade")}: ${gradeName}${
                        subjectLevel
                          ? `, ${t("common:subject_level")}: ${subjectLevel}`
                          : ""
                      }`,
                    }
                  : []),
              },
          sisData: { toddleId: id, thirdPartyIdList, isExcluded },
          isArchived,
          toddleExclusion: {
            isExcluded,
            toddleId: `TD-${id}`,
          },
          cellStyles: isProcessScreen
            ? {
                commonCellStyles: {
                  backgroundColor: isExcluded ? colors.surfaceWarning : "",
                },
              }
            : null,
        };
      });

      return feedData;
    }
    case "Staff": {
      const feedData = [];

      _.forEach(staff, staffData => {
        const id = _.get(staffData, "node.id", "");

        const name = getFormattedUserName({
          user: staffData.node,
          type: "staff",
        });

        const email = _.get(staffData, "node.email", "");

        const isArchived = getUserArchivalStatus(staffData.node);

        const currentMapping = _.first(
          _.filter(thirdPartyMapping, mapping => {
            return mapping.toddleId === id;
          })
        );
        const thirdPartyId = _.get(currentMapping, "thirdPartyId", "");

        const isExcluded = _.includes(excludedRowIds, `TD-${id}`);

        if (
          !_.includes(
            ["todden.superteacher@aischool.net", "support@toddleapp.com"],
            email
          )
        )
          feedData.push({
            id,
            isArchived,
            toddleData: { name: name, id: id, email: email },
            sisData: { toddleId: id, thirdPartyId: thirdPartyId, isExcluded },
            toddleExclusion: {
              isExcluded,
              toddleId: `TD-${id}`,
            },
            UID: `TD-${id}`,
            cellStyles: isProcessScreen
              ? {
                  commonCellStyles: {
                    backgroundColor: isExcluded ? colors.surfaceWarning : "",
                  },
                }
              : null,
          });
      });

      return feedData;
    }
    case "Student": {
      const feedData = _.map(students.edges, student => {
        const id = _.get(student, "node.id", "");

        const name = getFormattedUserName({
          user: student.node,
          type: "student",
        });

        const isArchived = getUserArchivalStatus(student.node);

        const email = _.get(student, "node.email", "");

        const yearGroup = _.get(student, "node.yearGroup", "");

        const alertTypes = _.get(student, "node.alertTypes", []);

        const grade = _.get(yearGroup, "grade", "");

        const currentMapping = _.first(
          _.filter(thirdPartyMapping, mapping => {
            return mapping.toddleId === id;
          })
        );

        const thirdPartyId = _.get(currentMapping, "thirdPartyId", "");

        const isExcluded = _.includes(excludedRowIds, `TD-${id}`);

        return {
          id,
          UID: id,
          toddleData: {
            name,
            id: id,
            email: email,
            studentAlertsConfig: {
              studentId: id,
              alertTypes,
            },
          },
          sisData: {
            toddleId: id,
            thirdPartyId: thirdPartyId,
            id: id,
            isExcluded,
          },
          isArchived,
          toddleExclusion: {
            isExcluded,
            toddleId: `TD-${id}`,
          },
          Grade: _.get(grade, "name", "-"),
          YearGroup: _.get(yearGroup, "name", "-"),
          cellStyles: isProcessScreen
            ? {
                commonCellStyles: {
                  backgroundColor: isExcluded ? colors.surfaceWarning : "",
                },
              }
            : null,
        };
      });

      return feedData;
    }
    case "Parent": {
      const feedData = _.map(familyData, data => {
        const id = _.get(data, "id", "");

        const name = getFormattedUserName({
          user: data,
          type: "parent",
        });

        const email = _.get(data, "email", "");

        const currentMapping = _.first(
          _.filter(thirdPartyMapping, mapping => {
            return mapping.toddleId === id;
          })
        );

        const thirdPartyId = _.get(currentMapping, "thirdPartyId", "");

        const isExcluded = _.includes(excludedRowIds, `TD-${id}`);

        return {
          id,
          toddleData: { name: name, id: id, email: email },
          sisData: {
            toddleId: id,
            thirdPartyId: thirdPartyId,
            id: id,
            isExcluded,
          },
          toddleExclusion: {
            isExcluded,
            toddleId: `TD-${id}`,
          },
          cellStyles: isProcessScreen
            ? {
                commonCellStyles: {
                  backgroundColor: isExcluded ? colors.surfaceWarning : "",
                },
              }
            : null,
          StudentName: `${data.studentFirstName} ${data.studentLastName}`,
        };
      });

      return feedData;
    }
    default:
      break;
  }
};

const _getClassMappings = ({ params, toddleId }) => {
  if (_.isEmpty(params)) {
    return [
      {
        toddleId: toddleId,
        thirdPartyId: "",
      },
    ];
  }
  return _.map(params, param => {
    return {
      toddleId: toddleId,
      thirdPartyId: param.id,
    };
  });
};

export const saveToddleSisMapping = ({
  service,
  params,
  toddleId,
  entityType,
  curriculumId,
  organizationId,
  variables,
  mappingState,
  activeTab,
  updateParentThirdPartyMapping,
  updateClassThirdPartyMapping,
  updateCourseCodeThirdPartyMapping,
  updateCourseThirdPartyMapping,
  updateLearningCourseThirdPartyMapping,
  updateStaffThirdPartyMapping,
  updateStudentThirdPartyMapping,
  refetchQueries,
  academicYearId,
}) => {
  const input = {
    service,
    entityType,
    curriculumId,
    academicYearId,
  };

  const mappings =
    activeTab === "Class" ||
    activeTab === "Course" ||
    activeTab === "BlueprintCourse" ||
    activeTab === "CourseCode"
      ? _getClassMappings({
          params,
          toddleId,
        })
      : {
          toddleId: toddleId,
          thirdPartyId: params ? params.id : "",
        };

  switch (activeTab) {
    case "CourseCode": {
      updateCourseCodeThirdPartyMapping({
        id: organizationId,
        input,
        mappings,
        variables,
        mappingState,
        activeTab,
      });
      break;
    }
    case "Course": {
      updateLearningCourseThirdPartyMapping({
        id: organizationId,
        input,
        mappings,
        variables,
        mappingState,
        activeTab,
      });
      break;
    }
    case "BlueprintCourse": {
      updateCourseThirdPartyMapping({
        id: organizationId,
        input,
        mappings,
        variables,
        mappingState,
        activeTab,
      });
      break;
    }
    case "Class": {
      updateClassThirdPartyMapping({
        id: organizationId,
        input,
        mappings,
        variables,
        mappingState,
        activeTab,
      });
      break;
    }
    case "Parent": {
      updateParentThirdPartyMapping({
        id: organizationId,
        input,
        mappings,
        refetchQueries,
      });
      break;
    }

    case "Staff": {
      updateStaffThirdPartyMapping({
        id: organizationId,
        input,
        mappings,
        variables,
        mappingState,
        activeTab,
      });
      break;
    }
    case "Student": {
      updateStudentThirdPartyMapping({
        id: organizationId,
        input,
        mappings,
        variables,
        mappingState,
        activeTab,
      });
      break;
    }
  }
};

export const getDropdownOptions = data =>
  _.chain(data)
    .map("parsedData")
    .filter(Boolean)
    .map(({ description = "", abbreviation = "" }) => ({
      label: description,
      value: abbreviation || description,
    }))
    .filter(option => option.label && option.value)
    .uniqBy("value")
    .value();

export const syncSettingsTemplate = ({
  t,
  curriculum,
  service,
  curriculumId,
  currentMetaData,
}) => {
  const isClassLinkPermEnabled = ACLStore.can(
    "FeatureFlag:ClassLinkParentSyncEnabled"
  );

  const showFamilyInviteEmailSettings = ACLStore.can(
    "FeatureFlag:AllowUsersToInviteParentsViaEmail"
  );

  const isLearningCourseEnabled = getIsLearningCoursesEnabled({
    curriculumProgramId: curriculumId,
  });

  const {
    showProfilePictureSync,
    useCurrentDateAsFromDate,
  } = getSyncSettingsValue({
    curriculumId,
    types: ["showProfilePictureSync", "useCurrentDateAsFromDate"],
  });

  if (isClassLinkPermEnabled) {
    PARENT_SYNC_ENABLED_SERVICES_LIST.push("CLASSLINK");
  }

  return {
    syncSettings: [
      ...(getIsAcademicCoursesEnabled(curriculumId, "blueprint")
        ? ["blueprint_course_settings"]
        : []),
      ...(isLearningCourseEnabled
        ? ["learning_course_settings", "academic_course_settings"]
        : []),
      "course_settings",
      "students_settings",
      "staff_settings",
      "parent_settings",
      "other_settings",
    ],

    other_settings: {
      title: "integration:other_settings",
      children: [
        "future_ay_sync_enabled",
        "sync_parents_in_future_ay",
        ...(isLearningCourseEnabled ? ["enable_blueprint_course_sync"] : []),
        "test_connection_on_gql",
        "toggle_classlink_parent_sync",
      ],
      isDevOnly: true,
    },
    students_settings: {
      title: "integration:students_settings",
      children: [
        "create_student_setting",
        "update_student_setting",
        "archive_student_setting",
        "preserve_student_setting",
        ...(showProfilePictureSync ? ["syncProfilePicture"] : []),
        "preserve_student_mapping_setting",
        "preserve_manual_student_mapping_setting",
        "enable_student_other_info",
        "custom_mobile_number_field",
        "studentUidFieldName",
        "filterStudentAsPerGrade",
        "createStudentFields",
        "updateStudentFields",
        "enableStudentSourceIdSync",
        "disableStudentAlertsModification",
        "disable_other_info_modification",
      ],
    },

    staff_settings: {
      title: "integration:staff_settings",
      showOnlyForTheseServices: STAFF_SYNC_ENABLED_SERVICES_LIST,
      children: [
        "create_staff_setting",
        "update_staff_setting",
        "archive_staff_setting",
        "preserve_staff_setting",
        "preserve_teacher_mapping_setting",
        "preserve_manual_teacher_mapping_setting",
        "create_no_course_teachers",
        "preserve_admin_mapping_setting",
        "createTeacherFields",
        "updateTeacherFields",
        "staffUidFieldName",
        "enableStaffSourceIdSync",
        ...(isLearningCourseEnabled
          ? ["removeCoursesFromStaffWhenRemovedFromClass"]
          : []),
        "showRemoveStaffFromCurriculumProgramWhenRemovedFromAllClasses",
        ...(_.get(
          currentMetaData,
          "showRemoveStaffFromCurriculumProgramWhenRemovedFromAllClasses",
          false
        )
          ? ["removeStaffFromCurriculumProgramWhenRemovedFromAllClasses"]
          : []),
      ],
    },

    blueprint_course_settings: {
      title: "course:blueprint_course_lowercase_plural",
      showOnlyForTheseServices: COURSE_SYNC_ENABLED_SERVICES_LIST,
      children: [
        "create_blueprint_course_setting",
        "update_blueprint_course_setting",
        "archive_blueprint_course_setting",
        "updateBlueprintCourseTitle",
        "editableBlueprintCourseFields",
        "createBlueprintCourseFields",
        "updateBlueprintCourseFields",
        "preserveManualBlueprintCourseMapping",
        "preserveAllBlueprintCourseMapping",
        "preserveManuallyCreatedBlueprintCourse",
      ],
    },

    learning_course_settings: {
      title: "common:teacher_course_plural",
      showOnlyForTheseServices: COURSE_SYNC_ENABLED_SERVICES_LIST,
      children: [
        "create_learning_course_setting",
        "update_learning_course_setting",
        "archive_learning_course_setting",
        "updateLearningCourseGrades",
        "updateLearningCourseTermIds",
        "updateLearningCourseSubjectsIds",
        "updateLearningCourseTitle",
        "considerClassesAsCourses",
        "useTermsAsGradingPeriods",
        "allowSubjectRemoval",
        "allowTermsRemoval",
        "editableLearningCourseFields",
        "createLearningCourseFields",
        "updateLearningCourseFields",
        "includeAllGradingPeriodEnabled",
        "includeSchoolLevelInCourseTitles",
        "includeTermInSplitCourseTitles",
        "preserveManuallyCreatedLearningCourse",
        "splitCoursesWhenClassesMappedHasDifferentTerms",
        "useSubjectAsCourses",
        "filter_as_per_school_level",
      ],
    },

    course_settings: {
      title: "integration:course_settings",
      showOnlyForTheseServices: COURSE_SYNC_ENABLED_SERVICES_LIST,
      children: [
        "create_course_setting",
        "update_course_setting",
        "archive_course_setting",
        "preserve_course_setting",
        ...(curriculum === CURRICULUM_TYPE_PYP ? [] : ["assign_subject"]),
        "isams_homeroom_course_settings",
        "updateCourseTitle",
        ...(isLearningCourseEnabled ? ["filterClassesAsPerGrade"] : []),
        "isams_subject_course_settings",
        "get_grade_from_student",
        "update_class_grades",
        "considerRollGroupsAsRawClasses",
        "considerTeachingAndRollGroupsBoth",
        "createCourseFields",
        "updateCourseFields",
        ...(isLearningCourseEnabled ? ["editableCourseFields"] : []),
        "createNoUserCourses",
        "allowMultipleGrades",
        "classUidFieldName",
        "enableCourseSourceIdSync",
        "disableCourseCreationOnNightlySync",
        "filter_by_campus_id",
        "useCurrentDateAsFromDate",
        "fromDate_course_sync",
        "toDate_course_sync",
      ],
    },

    academic_course_settings: {
      title: "integration:academic_course_settings",
      isDevOnly: true,
      children: [
        "create_academic_course_setting",
        "update_academic_course_setting",
        "archive_academic_course_setting",
        "academic_course_enabled",
      ],
    },

    parent_settings: {
      title: "integration:parents_settings",
      showOnlyForTheseServices: PARENT_SYNC_ENABLED_SERVICES_LIST,
      children: [
        "create_parent_setting",
        "update_parent_setting",
        "preserve_student_parent_mapping_setting",
        ...(showFamilyInviteEmailSettings
          ? [
              "toggle_invite_flow_for_parents", // used for Setting:IntegrationsInviteParentsViaEmail,
              "family_invite_email_language",
            ]
          : []),
        "parentUidFieldName",
        "enableParentSourceIdSync",
      ],
    },

    considerTeachingAndRollGroupsBoth: {
      label: t("integration:consider_teaching_and_roll_groups_both"),
      type: "toggle",
      key: "considerTeachingAndRollGroupsBoth",
      info: t("integration:consider_teaching_and_roll_groups_both_info"),
      enabled_services: ["CIVICA"],
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    considerRollGroupsAsRawClasses: {
      label: t("integration:consider_roll_groups_as_raw_classes"),
      type: "toggle",
      key: "considerRollGroupsAsRawClasses",
      info: t("integration:consider_roll_groups_as_raw_classes_info"),
      enabled_services: ["CIVICA"],
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    filterStudentAsPerGrade: {
      label: t("integration:filter_student_as_per_grade"),
      type: "toggle",
      key: "filterStudentAsPerGrade",
      info: t("integration:filter_student_as_per_grade_info"),
      enabled_services: ["CIVICA"],
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    create_student_setting: {
      label: t("integration:create_students_setting_msg"),
      type: "toggle",
      key: "createStudent",
      info: t("integration:create_student_setting_info"),
    },
    enable_student_other_info: {
      label: t("integration:enable_other_info_setting_label"),
      type: "toggle",
      key: "studentsOtherInfoEnabled",
      info: t("integration:enable_other_info_setting_info"),
      enabled_services: ["OPENAPPLY"],
    },
    custom_mobile_number_field: {
      label: t("integration:custom_mobile_number_field_label"),
      type: "textInput",
      key: "customMobileNumberField",
      info: t("integration:custom_mobile_number_field_info"),
      inputConfig: {
        type: "tel",
      },
      enabled_services: ["ISAMS"],
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    update_student_setting: {
      label: t("integration:update_students_setting_msg"),
      type: "toggle",
      key: "updateStudent",
      info: t("integration:update_student_setting_info"),
    },

    archive_student_setting: {
      label: t("integration:archive_students_setting_msg"),
      type: "toggle",
      key: "deleteStudent",
      info: t("integration:archive_student_setting_info"),
    },

    preserve_student_setting: {
      label: `${t("integration:create_form_portal_msg_with_label", {
        label: t("common:lowercase", { text: t("common:student") }),
      })}`,
      type: "toggle",
      key: "syncOnlyRosteredStudents",
      cacheKey: "student",
      info: t("integration:preserve_student_created_from_portal_info"),
    },

    preserve_student_mapping_setting: {
      label: `${t("integration:preserve_all_mapping")}`,
      type: "toggle",
      key: "preserveStudentMapping",
      info: t("integration:preserve_student_mapping_info"),
    },

    preserve_manual_student_mapping_setting: {
      label: `${t("integration:preserve_manual_mapping")}`,
      type: "toggle",
      key: "preserveStudentManualMapping",
      info: t("integration:preserve_manual_student_mapping_info"),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    preserve_teacher_mapping_setting: {
      label: `${t("integration:preserve_all_mapping")}`,
      type: "toggle",
      key: "preserveTeacherMapping",
      info: t("integration:preserve_staff_mapping_info"),
    },

    preserve_manual_teacher_mapping_setting: {
      label: `${t("integration:preserve_manual_mapping")}`,
      type: "toggle",
      key: "preserveTeacherManualMapping",
      info: t("integration:preserve_manual_staff_mapping_info"),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    preserve_student_parent_mapping_setting: {
      label: `${t("integration:preserve_parent_child_mapping")}`,
      type: "toggle",
      key: "preserveParentStudentMapping",
      info: t("integration:preserve_parent_child_mapping_info"),
    },

    create_staff_setting: {
      label: t("integration:create_staff_setting_msg"),
      type: "toggle",
      key: "createTeacher",
      info: t("integration:create_staff_setting_info"),
    },

    update_staff_setting: {
      label: t("integration:update_staff_setting_msg"),
      type: "toggle",
      key: "updateTeacher",
      info: t("integration:update_staff_setting_info"),
    },

    archive_staff_setting: {
      label: t("integration:archive_staff_setting_msg"),
      type: "toggle",
      key: "deleteTeacher",
      info: t("integration:archive_staff_setting_info"),
    },

    preserve_staff_setting: {
      label: `${t("integration:create_form_portal_msg_with_label", {
        label: t("common:lowercase", { text: t("common:staff") }),
      })}`,
      type: "toggle",
      key: "syncOnlyRosteredTeachers",
      cacheKey: "teacher",
      info: t("integration:preserve_staff_created_from_portal_info"),
    },

    create_course_setting: {
      label: t("integration:create_course_setting_msg"),
      type: "toggle",
      key: "createCourse",
      info: t("integration:create_course_setting_info"),
    },

    create_learning_course_setting: {
      label: t("integration:create_learning_course_setting_msg"),
      type: "toggle",
      key: "createLearningCourse",
      info: t("integration:create_teacher_learning_course_setting_info"),
    },

    create_blueprint_course_setting: {
      label: t("integration:create_blueprint_course_setting_msg"),
      type: "toggle",
      key: "createBlueprintCourse",
      info: t("integration:create_blueprint_course_setting_info"),
    },

    considerClassesAsCourses: {
      label: t("integration:consider_classes_as_teacher_courses_msg"),
      type: "toggle",
      key: "considerClassesAsCourses",
      info: t("integration:consider_classes_as_teacher_courses_info"),
      permission: "Common:EnableExtraRosterSyncSettings",
      isDevUserOnly: true,
    },

    useTermsAsGradingPeriods: {
      label: t("integration:use_terms_as_grading_periods_msg"),
      type: "toggle",
      key: "useTermsAsGradingPeriods",
      enabled_services: ["CLEVER"],
      info: t("integration:use_terms_as_grading_periods_info"),
      permission: "Common:EnableExtraRosterSyncSettings",
      isDevUserOnly: true,
    },

    allowSubjectRemoval: {
      label: t("integration:allow_subject_removal_msg"),
      type: "toggle",
      key: "allowSubjectRemoval",
      info: t("integration:allow_subject_removal_info"),
      permission: "Common:EnableExtraRosterSyncSettings",
      isDevUserOnly: true,
    },

    allowTermsRemoval: {
      label: t("integration:allow_terms_removal_msg"),
      type: "toggle",
      key: "allowTermsRemoval",
      info: t("integration:allow_term_removal_info"),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    update_course_title: {
      label: t("integration:update_course_title_msg"),
      type: "toggle",
      key: "updateCourseTitle",
      info: t("integration:update_course_title_info"),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    update_course_setting: {
      label: t("integration:update_course_setting_msg"),
      type: "toggle",
      key: "updateCourse",
      info: t("integration:update_course_setting_info_with_service", {
        service,
      }),
    },

    filterClassesAsPerGrade: {
      label: t("integration:filter_classes_as_per_grade_msg"),
      type: "toggle",
      key: "filterClassesAsPerGrade",
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
      info: t("integration:filter_classes_as_per_grade_info"),
    },

    updateCourseTitle: {
      label: t("integration:update_course_title_msg"),
      type: "toggle",
      cacheKey: "updateCourse",
      dbFieldKey: "updateCourseFields",
      key: "updateCourseTitleField",
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
      info: t("integration:update_course_title_info"),
    },

    update_learning_course_setting: {
      label: t("integration:update_teacher_learning_course_setting_msg"),
      type: "toggle",
      key: "updateLearningCourse",
      info: t(
        "integration:update_teacher_learning_course_setting_info_with_service",
        {
          service,
        }
      ),
    },

    update_blueprint_course_setting: {
      label: t("integration:update_teacher_blueprint_course_setting_msg"),
      type: "toggle",
      key: "updateBlueprintCourse",
      info: t(
        "integration:update_teacher_blueprint_course_setting_info_with_service",
        {
          service,
        }
      ),
    },

    archive_course_setting: {
      label: t("integration:delete_course_setting_msg"),
      type: "toggle",
      key: "deleteCourse",
      info: t("integration:archive_course_setting_info"),
    },

    archive_learning_course_setting: {
      label: t("integration:delete_teacher_learning_course_setting_msg"),
      type: "toggle",
      key: "archiveLearningCourse",
      info: t("integration:archive_teacher_learning_course_setting_info"),
    },

    archive_blueprint_course_setting: {
      label: t("integration:archive_blueprint_course_setting_msg"),
      type: "toggle",
      key: "archiveBlueprintCourse",
      info: t("integration:archive_blueprint_course_setting_info"),
    },

    preserveManualBlueprintCourseMapping: {
      label: t("integration:preserve_manual_blueprint_course_mappings_msg"),
      type: "toggle",
      key: "preserveManualBlueprintCourseMapping",
      info: t(
        "integration:preserve_manual_blueprint_course_mapping_setting_info"
      ),
    },

    preserveAllBlueprintCourseMapping: {
      label: t("integration:preserve_all_blueprint_course_mapping_msg"),
      type: "toggle",
      key: "preserveAllBlueprintCourseMapping",
      info: t("integration:preserve_all_blueprint_course_mapping_setting_info"),
    },

    preserveManuallyCreatedBlueprintCourse: {
      label: t("integration:preserve_manually_created_blueprint_course_msg"),
      type: "toggle",
      key: "preserveManuallyCreatedBlueprintCourse",
      info: t(
        "integration:preserve_manually_created_blueprint_course_setting_info"
      ),
    },

    updateLearningCourseGrades: {
      label: t("integration:update_teacher_learning_course_grade_setting_msg"),
      type: "toggle",
      isDevUserOnly: true,
      dbFieldKey: "updateLearningCourseFields",
      cacheKey: "updateLearningCourse",
      key: "updateLearningCourseGrade",
      info: t(
        "integration:update_teacher_learning_course_update_setting_info",
        {
          label: t("common:grade"),
        }
      ),
    },

    updateBlueprintCourseGrades: {
      label: t("integration:update_teacher_blueprint_course_grade_setting_msg"),
      type: "toggle",
      isDevUserOnly: true,
      dbFieldKey: "updateBlueprintCourseFields",
      cacheKey: "updateBlueprintCourse",
      key: "updateBlueprintCourseGrade",
      info: t(
        "integration:update_teacher_blueprint_course_update_setting_info",
        {
          label: t("common:grade"),
        }
      ),
    },

    updateLearningCourseTermIds: {
      label: t(
        "integration:update_teacher_learning_course_grading_period_setting_msg"
      ),
      type: "toggle",
      isDevUserOnly: true,
      dbFieldKey: "updateLearningCourseFields",
      cacheKey: "updateLearningCourse",
      key: "updateLearningCourseTermIds",
      info: t(
        "integration:update_teacher_learning_course_update_setting_info",
        {
          label: t("common:grading_period"),
        }
      ),
    },

    updateBlueprintCourseTermIds: {
      label: t(
        "integration:update_teacher_blueprint_course_grading_period_setting_msg"
      ),
      type: "toggle",
      isDevUserOnly: true,
      dbFieldKey: "updateBlueprintCourseFields",
      cacheKey: "updateBlueprintCourse",
      key: "updateBlueprintCourseTermIds",
      info: t(
        "integration:update_teacher_blueprint_course_update_setting_info",
        {
          label: t("common:grading_period"),
        }
      ),
    },

    updateLearningCourseSubjectsIds: {
      label: t(
        "integration:update_teacher_learning_course_subject_setting_msg"
      ),
      type: "toggle",
      isDevUserOnly: true,
      dbFieldKey: "updateLearningCourseFields",
      cacheKey: "updateLearningCourse",
      key: "updateLearningCourseSubjectsIds",
      info: t(
        "integration:update_teacher_learning_course_update_setting_info",
        {
          label: t("common:SUBJECT"),
        }
      ),
    },

    updateBlueprintCourseSubjectsIds: {
      label: t(
        "integration:update_teacher_blueprint_course_subject_setting_msg"
      ),
      type: "toggle",
      isDevUserOnly: true,
      dbFieldKey: "updateBlueprintCourseFields",
      cacheKey: "updateBlueprintCourse",
      key: "updateBlueprintCourseSubjectsIds",
      info: t(
        "integration:update_teacher_blueprint_course_update_setting_info",
        {
          label: t("common:SUBJECT"),
        }
      ),
    },

    updateLearningCourseTitle: {
      label: t("integration:update_teacher_learning_course_title_setting_msg"),
      type: "toggle",
      dbFieldKey: "updateLearningCourseFields",
      cacheKey: "updateLearningCourse",
      key: "updateLearningCourseTitle",
      info: t(
        "integration:update_teacher_learning_course_update_setting_info",
        {
          label: t("common:title"),
        }
      ),
    },

    updateBlueprintCourseTitle: {
      label: t("integration:update_teacher_blueprint_course_title_setting_msg"),
      type: "toggle",
      dbFieldKey: "updateBlueprintCourseFields",
      cacheKey: "updateBlueprintCourse",
      key: "updateBlueprintCourseTitle",
      info: t(
        "integration:update_teacher_blueprint_course_update_setting_info",
        {
          label: t("common:title"),
        }
      ),
    },

    preserve_course_setting: {
      label: `${t("integration:create_form_portal_msg_with_label", {
        label: t("common:lowercase", { text: t("common:course_plural") }),
      })}`,
      type: "toggle",
      key: "syncOnlyRosteredCourses",
      cacheKey: "course",
      info: t("integration:preserve_class_created_from_portal_info"),
    },

    create_no_course_students: {
      label: `${t("integration:create_no_course_student")}`,
      type: "toggle",
      key: "createNoCourseStudents",
      info: t("integration:create_no_course_student_info"),
    },
    create_no_course_teachers: {
      label: `${t("integration:create_no_course_teacher")}`,
      type: "toggle",
      key: "createNoCourseTeachers",
      info: t("integration:create_no_course_staff_info"),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },
    assign_subject: {
      label: `${t("integration:assign_subject_toggle_msg")}`,
      type: "toggle",
      key: "assignSubject",
      info: t("integration:assign_subject_info"),
    },
    create_parent_setting: {
      label: t("integration:create_parent_setting_msg"),
      type: "toggle",
      key: "createParent",
      info: t("integration:create_parent_setting_info"),
    },

    update_parent_setting: {
      label: t("integration:update_parent_setting_msg"),
      type: "toggle",
      key: "updateParent",
      info: t("integration:update_parent_setting_info"),
    },

    get_grade_from_student: {
      label: t("integration:get_grade_from_student_label"),
      type: "toggle",
      key: "getGradesFromStudent",
      info: t("integration:get_grade_from_student_info"),
      enabled_services: [
        "POWERSCHOOL",
        "CLASSLINK",
        "TASS",
        "CIVICA",
        "SENTRAL",
        "SYNERGETIC",
        "WONDE",
      ],
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    isams_homeroom_course_settings: {
      label: t("integration:isams_homeroom_course_settings_label"),
      type: "toggle",
      key: "Homeroom",
      info: t("integration:isams_homeroom_course_settings_info"),
      enabled_services: ["ISAMS"],
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    isams_subject_course_settings: {
      label: t("integration:isams_subject_course_settings_label"),
      type: "toggle",
      key: "Subject",
      info: t("integration:isams_subject_course_settings_info"),
      enabled_services: ["ISAMS"],
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    preserve_admin_mapping_setting: {
      label: t("integration:preserve_admin_mapping_label"),
      type: "toggle",
      key: "preserveAdminMapping",
      info: t("integration:preserve_admin_mapping_info"),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    removeCoursesFromStaffWhenRemovedFromClass: {
      label: t("integration:remove_courses_from_staff_when_removed_from_class"),
      type: "toggle",
      key: "removeCoursesFromStaffWhenRemovedFromClass",
      info: t(
        "integration:remove_courses_from_staff_when_removed_from_class_info"
      ),
    },

    showRemoveStaffFromCurriculumProgramWhenRemovedFromAllClasses: {
      label: t(
        "integration:show_remove_staff_from_curriculum_when_removed_from_all_classes"
      ),
      type: "toggle",
      key: "showRemoveStaffFromCurriculumProgramWhenRemovedFromAllClasses",
      info: t(
        "integration:show_remove_staff_from_curriculum_when_removed_from_all_classes_info"
      ),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    removeStaffFromCurriculumProgramWhenRemovedFromAllClasses: {
      label: t(
        "integration:remove_staff_from_curriculum_when_removed_from_all_classes"
      ),
      type: "toggle",
      key: "removeStaffFromCurriculumProgramWhenRemovedFromAllClasses",
      info: t(
        "integration:remove_staff_from_curriculum_when_removed_from_all_classes_info"
      ),
    },

    future_ay_sync_enabled: {
      label: t("integration:future_academic_year_sync"),
      type: "toggle",
      key: "futureAYSyncEnabled",
      info: t("integration:future_academic_year_sync_info"),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    enable_blueprint_course_sync: {
      label: t("integration:enable_blueprint_course_sync"),
      type: "toggle",
      key: "enableBlueprintCourseSync",
      info: t("integration:enable_blueprint_course_sync_info"),
      isDevUserOnly: true,
    },

    sync_parents_in_future_ay: {
      label: t("integration:future_academic_year_sync_parent"),
      type: "toggle",
      key: "syncParentInFutureAY",
      info: t("integration:future_academic_year_sync_parent_info"),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    disable_other_info_modification: {
      label: t("integration:exclude_other_info_from_sync"),
      type: "toggle",
      info: t("integration:exclude_other_info_info"),
      key: "disableOtherInfoModification",
      permission: "FeatureFlag:ToggleEntityModificationFromSync",
    },

    syncProfilePicture: {
      label: t("integration:sync_profile_picture_label"),
      type: "toggle",
      info: t("integration:sync_profile_picture_info_with_label", {
        service,
      }),
      key: "syncProfilePicture",
      enabled_services: ["CIVICA", "SENTRAL", "ISAMS"],
    },

    toggle_invite_flow_for_parents: {
      label: t("integration:invite_family_members_via_email_setting"),
      type: "toggle",
      key: "inviteParentsViaEmail",
      info: t("integration:invite_family_members_via_email_setting_info"),
      isConfiguration: true,
      configLevel: "curriculum",
      settingName: "Setting:IntegrationsInviteParentsViaEmail",
    },

    toggle_classlink_parent_sync: {
      label: t("integration:toggle_parent_sync"),
      type: "toggle",
      key: "classlinkParentSync",
      info: t("integration:toggle_parent_sync_info"),
      isConfiguration: true,
      isDevUserOnly: true,
      settingName: "FeatureFlag:ClassLinkParentSyncEnabled",
      configLevel: "organization",
      enabled_services: ["CLASSLINK"],
    },

    test_connection_on_gql: {
      label: t("integration:test_connection_on_graphql_label"),
      type: "toggle",
      key: "testConnectionOnGql",
      info: t("integration:test_connection_on_graphql_info"),
      isConfiguration: true,
      isDevUserOnly: true,
      configLevel: "organization",
      settingName: "FeatureFlag:EnableTestConnectionOnGraphql",
    },

    family_invite_email_language: {
      label: t("integration:family_members_invitation_language"),
      type: "dropdown",
      key: "familyInvitationLanguage",
      info: t("integration:family_members_invitation_language_info"),
      inputConfig: {
        options: FAMILY_INVITE_EMAIL_LANGUAGE_OPTIONS,
      },
    },
    update_class_grades: {
      label: t("integration:update_class_grades_sync_setting"),
      type: "toggle",
      key: "shouldUpdateClassGrade",
      info: t("integration:update_class_grades_sync_setting_info"),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },
    studentUidFieldName: {
      label: t("integration:student_uid_field_name"),
      type: "textInput",
      key: "studentUidFieldName",
      info: t("integration:student_uid_field_name_info"),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    staffUidFieldName: {
      label: t("integration:entity_uid_field_name_with_label", {
        label: t("common:staff"),
      }),
      type: "textInput",
      key: "staffUidFieldName",
      info: t("integration:entity_uid_field_name_info_with_label", {
        label: t("common:staff"),
      }),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    classUidFieldName: {
      label: t("integration:entity_uid_field_name_with_label", {
        label: t("common:class"),
      }),
      type: "textInput",
      key: "classUidFieldName",
      info: t("integration:entity_uid_field_name_info_with_label", {
        label: t("common:class_lowercase"),
      }),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    parentUidFieldName: {
      label: t("integration:entity_uid_field_name_with_label", {
        label: t("common:parent"),
      }),
      type: "textInput",
      key: "parentUidFieldName",
      info: t("integration:entity_uid_field_name_info_with_label", {
        label: t("common:parent"),
      }),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    createNoUserCourses: {
      label: t("integration:create_no_user_courses"),
      type: "toggle",
      key: "createNoUserCourses",
      info: t("integration:create_no_user_courses_info"),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
      enabled_services: ["CIVICA"],
    },

    allowMultipleGrades: {
      label: t("integration:allow_multiple_grades"),
      type: "toggle",
      key: "allowMultipleGrades",
      info: t("integration:allow_multiple_grades_info"),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
      enabled_services: ["CIVICA"],
    },

    enableStudentSourceIdSync: {
      label: t("integration:enable_source_id_sync_with_label", {
        label: t("common:student_lowercase"),
      }),
      type: "toggle",
      key: "enableStudentSourceIdSync",
      info: t("integration:enable_source_id_sync_with_info_with_label", {
        label: t("common:student_lowercase"),
      }),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    disableStudentAlertsModification: {
      label: t("integration:exclude_student_alerts_from_sync"),
      type: "toggle",
      key: "disableStudentAlertsModification",
      info: t("integration:exclude_student_alerts_info"),
      permission: "FeatureFlag:ToggleEntityModificationFromSync",
    },

    enableCourseSourceIdSync: {
      label: t("integration:enable_source_id_sync_with_label", {
        label: t("common:class_lowercase"),
      }),
      type: "toggle",
      key: "enableCourseSourceIdSync",
      info: t("integration:enable_source_id_sync_with_info_with_label", {
        label: t("common:class_lowercase"),
      }),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    disableCourseCreationOnNightlySync: {
      label: t("integration:disable_course_creation_in_nightly_sync"),
      type: "toggle",
      key: "disableCourseCreationOnNightlySync",
      info: t("integration:disable_course_creation_info"),
      permission: "FeatureFlag:ToggleEntityModificationFromSync",
    },

    enableParentSourceIdSync: {
      label: t("integration:enable_source_id_sync_with_label", {
        label: t("common:parent_lowercase"),
      }),
      type: "toggle",
      key: "enableParentSourceIdSync",
      info: t("integration:enable_source_id_sync_with_info_with_label", {
        label: t("common:parent_lowercase"),
      }),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    enableStaffSourceIdSync: {
      label: t("integration:enable_source_id_sync_with_label", {
        label: t("common:staff"),
      }),
      type: "toggle",
      key: "enableStaffSourceIdSync",
      info: t("integration:enable_source_id_sync_with_info_with_label", {
        label: t("common:staff"),
      }),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    createStudentFields: {
      label: t("integration:create_entity_field_with_label", {
        label: t("common:student_lowercase"),
      }),
      type: "dropdown",
      key: "createStudentFields",
      info: t("integration:create_entity_field_with_info", {
        label: t("common:student_lowercase"),
      }),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
      inputConfig: {
        options: [
          { label: "Do not update any thing", value: "DO_NOT_UPDATE_ANYTHING" },
        ], // Add options as needed
        allowAddValue: true,
        isMulti: true,
      },
    },
    updateStudentFields: {
      label: t("integration:update_entity_field_with_label", {
        label: t("common:student"),
      }),
      type: "dropdown",
      key: "updateStudentFields",
      info: t("integration:update_entity_field_with_info", {
        label: t("common:student"),
      }),
      inputConfig: {
        options: [
          { label: "Do not update any thing", value: "DO_NOT_UPDATE_ANYTHING" },
        ], // Add options as needed
        allowAddValue: true,
        isMulti: true,
      },
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },
    createTeacherFields: {
      label: t("integration:create_entity_field_with_label", {
        label: t("common:staff"),
      }),
      type: "dropdown",
      key: "createTeacherFields",
      info: t("integration:create_entity_field_with_label", {
        label: t("common:staff"),
      }),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
      inputConfig: {
        options: [
          { label: "Do not update any thing", value: "DO_NOT_UPDATE_ANYTHING" },
        ], // Add options as needed
        allowAddValue: true,
        isMulti: true,
      },
    },
    updateTeacherFields: {
      label: t("integration:update_entity_field_with_label", {
        label: t("common:staff"),
      }),
      type: "dropdown",
      key: "updateTeacherFields",
      info: t("integration:update_entity_field_with_label", {
        label: t("common:staff"),
      }),
      inputConfig: {
        options: [
          { label: "Do not update any thing", value: "DO_NOT_UPDATE_ANYTHING" },
        ], // Add options as needed
        allowAddValue: true,
        isMulti: true,
      },
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },
    createCourseFields: {
      label: t("integration:create_entity_field_with_label", {
        label: t("common:class"),
      }),
      type: "dropdown",
      key: "createCourseFields",
      dbFieldKey: "createCourse",
      info: t("integration:create_entity_field_with_label", {
        label: t("common:class"),
      }),
      inputConfig: {
        options: [
          { label: "Do not update any thing", value: "DO_NOT_UPDATE_ANYTHING" },
        ], // Add options as needed
        allowAddValue: true,
        isMulti: true,
      },
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    createLearningCourseFields: {
      label: t("integration:create_entity_field_with_label", {
        label: t("common:teacher_course"),
      }),
      type: "dropdown",
      key: "createLearningCourseFields",
      dbFieldKey: "createLearningCourse",
      info: t("integration:create_entity_field_with_label", {
        label: t("common:teacher_course"),
      }),
      inputConfig: {
        options: [
          { label: "Do not update any thing", value: "DO_NOT_UPDATE_ANYTHING" },
        ], // Add options as needed
        allowAddValue: true,
        isMulti: true,
      },
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    createBlueprintCourseFields: {
      label: t("integration:create_entity_field_with_label", {
        label: t("course:blueprint_course_lowercase"),
      }),
      type: "dropdown",
      key: "createBlueprintCourseFields",
      dbFieldKey: "createBlueprintCourse",
      info: t("integration:create_entity_field_with_label", {
        label: t("course:blueprint_course_lowercase"),
      }),
      inputConfig: {
        options: [
          { label: "Do not update any thing", value: "DO_NOT_UPDATE_ANYTHING" },
        ], // Add options as needed
        allowAddValue: true,
        isMulti: true,
      },
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    updateCourseFields: {
      label: t("integration:update_entity_field_with_label", {
        label: t("common:class"),
      }),
      type: "dropdown",
      key: "updateCourseFields",
      dbFieldKey: "updateCourse",
      info: t("integration:update_entity_field_with_label", {
        label: t("common:class"),
      }),
      inputConfig: {
        options: [
          { label: "Do not update any thing", value: "DO_NOT_UPDATE_ANYTHING" },
        ], // Add options as needed
        allowAddValue: true,
        isMulti: true,
      },
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    updateLearningCourseFields: {
      label: t("integration:update_entity_field_with_label", {
        label: t("common:teacher_course"),
      }),
      type: "dropdown",
      key: "updateLearningCourseFields",
      dbFieldKey: "updateLearningCourse",
      info: t("integration:update_entity_field_with_label", {
        label: t("common:teacher_course"),
      }),
      inputConfig: {
        options: [
          { label: "Do not update any thing", value: "DO_NOT_UPDATE_ANYTHING" },
        ], // Add options as needed
        allowAddValue: true,
        isMulti: true,
      },
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    updateBlueprintCourseFields: {
      label: t("integration:update_entity_field_with_label", {
        label: t("course:blueprint_course_lowercase"),
      }),
      type: "dropdown",
      key: "updateBlueprintCourseFields",
      dbFieldKey: "updateBlueprintCourse",
      info: t("integration:update_entity_field_with_label", {
        label: t("course:blueprint_course_lowercase"),
      }),
      inputConfig: {
        options: [
          { label: "Do not update any thing", value: "DO_NOT_UPDATE_ANYTHING" },
        ], // Add options as needed
        allowAddValue: true,
        isMulti: true,
      },
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    editableLearningCourseFields: {
      label: t("integration:editable_entity_field_with_label", {
        label: t("common:teacher_course"),
      }),
      type: "dropdown",
      key: "editableLearningCourseFields",
      info: t("integration:editable_entity_field_with_label", {
        label: t("common:teacher_course"),
      }),
      inputConfig: {
        options: [
          { label: "Terms", value: "term" },
          { label: "Subject", value: "subjectIds" },
          { label: "Grade", value: "grade" },
          { label: "Course Code", value: "courseCode" },
          { label: "Blueprint Course", value: "blueprintCourse" },
          { label: "Edit no fields", value: "EDIT_NO_FIELDS" },
        ], // Add options as needed
        allowAddValue: false,
        isMulti: true,
      },
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    editableBlueprintCourseFields: {
      label: t("integration:editable_entity_field_with_label", {
        label: t("course:blueprint_course_lowercase"),
      }),
      type: "dropdown",
      key: "editableBlueprintCourseFields",
      info: t("integration:editable_entity_field_with_label", {
        label: t("course:blueprint_course_lowercase"),
      }),
      inputConfig: {
        options: [
          { label: "Terms", value: "term" },
          { label: "Subject", value: "subjectIds" },
          { label: "Grade", value: "grade" },
          { label: "Course Code", value: "courseCode" },
          { label: "Edit no fields", value: "EDIT_NO_FIELDS" },
        ], // Add options as needed
        allowAddValue: false,
        isMulti: true,
      },
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    editableCourseFields: {
      label: t("integration:editable_entity_field_with_label", {
        label: t("common:class"),
      }),
      type: "dropdown",
      key: "editableClassFields",
      info: t("integration:editable_entity_field_with_label", {
        label: t("common:class"),
      }),
      inputConfig: {
        options: [
          { label: "Course", value: "course" },
          { label: "Edit no fields", value: "EDIT_NO_FIELDS" },
        ], // Add options as needed
        allowAddValue: false,
        isMulti: true,
      },
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    includeAllGradingPeriodEnabled: {
      label: t("integration:include_all_grading_period_label"),
      type: "EnhancedDropdown",
      inputConfig: {
        isMulti: true,
        allowAddValue: true,
      },
      key: "includeAllGradingPeriodEnabled",
      info: t("integration:include_all_grading_period_info"),
      permission: "Common:EnableExtraRosterSyncSettings",
    },
    includeSchoolLevelInCourseTitles: {
      label: t(
        "integration:include_school_level_in_teacher_course_titles_label"
      ),
      type: "toggle",
      key: "includeSchoolLevelInCourseTitles",
      info: t("integration:include_school_level_in_teacher_course_titles_info"),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },
    includeTermInSplitCourseTitles: {
      label: t("integration:include_term_in_split_teacher_course_titles_label"),
      type: "toggle",
      key: "includeTermInSplitCourseTitles",
      info: t("integration:include_term_in_split_teacher_course_titles_info"),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    preserveManuallyCreatedLearningCourse: {
      label: t("integration:preserve_manually_created_teacher_course_msg"),
      type: "toggle",
      key: "preserveManuallyCreatedLearningCourse",
      info: t(
        "integration:preserve_manually_created_teacher_course_setting_info"
      ),
    },
    splitCoursesWhenClassesMappedHasDifferentTerms: {
      label: t(
        "integration:split_courses_when_classes_mapped_has_different_terms_label"
      ),
      type: "toggle",
      key: "splitCoursesWhenClassesMappedHasDifferentTerms",
      info: t(
        "integration:split_courses_when_classes_mapped_has_different_terms_info"
      ),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },

    useSubjectAsCourses: {
      label: t("integration:use_subject_as_courses_label"),
      type: "toggle",
      key: "useSubjectAsCourses",
      info: t("integration:use_subject_as_courses_info"),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
      enabled_services: ["TASS", "COMPASS"],
    },

    create_academic_course_setting: {
      label: t("integration:create_academic_course_msg"),
      type: "toggle",
      key: "createAcademicCourse",
      info: t("integration:create_academic_course_info"),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },
    update_academic_course_setting: {
      label: t("integration:update_academic_course_msg"),
      type: "toggle",
      key: "updateAcademicCourse",
      info: t("integration:update_academic_course_info"),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },
    archive_academic_course_setting: {
      label: t("integration:archive_academic_course_msg"),
      type: "toggle",
      key: "archiveAcademicCourse",
      info: t("integration:archive_academic_course_info"),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },
    academic_course_enabled: {
      label: t("integration:academic_course_enabled_label"),
      type: "toggle",
      key: "isAcademicCourseEnabled",
      info: t("integration:academic_course_enabled_info"),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },
    filter_by_campus_id: {
      label: t("integration:filter_by_campus_id_label"),
      type: "toggle",
      key: "filterByCampusId",
      enabled_services: ["VERACROSSV3"],
      info: t("integration:filter_by_campus_id_info"),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },
    useCurrentDateAsFromDate: {
      label: t("integration:use_current_date_as_from_date_label"),
      type: "toggle",
      key: "useCurrentDateAsFromDate",
      info: t("integration:use_current_date_as_from_date_info"),
      enabled_services: ["CIVICA"],
    },
    fromDate_course_sync: {
      label: t("integration:from_date_course_sync_label"),
      type: "date",
      key: "fromDate",
      info: t("integration:from_date_course_sync_info"),
      enabled_services: ["CIVICA"],
      isReadOnly: useCurrentDateAsFromDate, // make this setting readonly when useCurrentDateAsFromDate is true
      readOnlyDisplayValue: moment().format("YYYY-MM-DD"),
    },
    toDate_course_sync: {
      label: t("integration:to_date_course_sync_label"),
      type: "date",
      key: "toDate",
      info: t("integration:to_date_course_sync_info"),
      enabled_services: ["CIVICA"],
    },
    filter_as_per_school_level: {
      label: t("integration:filter_as_per_school_level_label"),
      type: "toggle",
      key: "filterAsPerSchoolLevel",
      enabled_services: ["VERACROSSV3"],
      info: t("integration:filter_as_per_school_level_info"),
      isDevUserOnly: true,
      permission: "Common:EnableExtraRosterSyncSettings",
    },
  };
};

export const getSelectedTabText = ({ activeTab, t }) => {
  switch (activeTab) {
    case "CourseCodes":
      return "integration:selected_course_code_with_count";
    case "BlueprintCourses":
      return "common:selected_blueprint_course_with_count";
    case "Courses":
      return "common:selected_teacher_course_with_count";
    case "Classes":
      return "common:class_selected_with_count";
    case "Students":
      return "common:students_selected_with_count";

    case "Parents":
      return "integration:selected_parent_count";

    case "Staff":
      return "common:staff_selected_with_count";

    default:
      return [];
  }
};

export const getExistingDataMappingFiltersList = ({ t }) => {
  return [
    {
      label: t("common:all"),
      value: "ALL",
      key: "ALL",
    },
    {
      label: t("integration:mapped"),
      value: "MAPPED",
      key: "MAPPED",
    },
    {
      label: t("integration:unmapped"),
      value: "UNMAPPED",
      key: "UNMAPPED",
    },
  ];
};

export const getApiFiltersTemplateForService = ({ service, t }) => {
  switch (service) {
    case "VERACROSS":
      return {
        apiFilters: [
          "veracross_course_status",
          "veracross_student_grade_level",
          "veracross_student_options",
        ],

        veracross_course_status: {
          label: t("integration:veracross_course_status_label"),
          type: "multiSelect",
          key: "veracrossCoursesStatus",
          info: t("integration:veracross_course_status_info"),
          enabled_services: ["VERACROSS"],
        },

        veracross_student_options: {
          label: t("integration:veracross_student_options_label"),
          type: "input",
          key: "veracrossStudentOption",
          info: t("integration:veracross_student_options_info"),
          enabled_services: ["VERACROSS"],
        },

        veracross_student_grade_level: {
          label: t("integration:veracross_student_grade_level_label"),
          type: "multiSelect",
          key: "veracrossStudentGradeLevel",
          info: t("integration:veracross_student_grade_level_info"),
          enabled_services: ["VERACROSS"],
        },
      };
    case "VERACROSSV3":
      return {
        apiFilters: [
          "veracross_course_status",
          "veracross_student_grade_level",
          "veracross_student_options",
        ],

        veracross_course_status: {
          label: t("integration:veracross_course_status_label"),
          type: "multiSelect",
          key: "veracrossCoursesStatus",
          info: t("integration:veracross_course_status_info"),
          enabled_services: ["VERACROSS"],
        },

        veracross_student_options: {
          label: t("integration:veracross_student_options_label"),
          type: "input",
          key: "veracrossStudentOption",
          info: t("integration:veracross_student_options_info"),
          enabled_services: ["VERACROSS"],
        },

        veracross_student_grade_level: {
          label: t("integration:veracross_student_grade_level_label"),
          type: "multiSelect",
          key: "veracrossStudentGradeLevel",
          info: t("integration:veracross_student_grade_level_info"),
          enabled_services: ["VERACROSS"],
        },
      };

    default:
      return [];
  }
};

export const getIsAcronymRequired = curriculumKey => {
  return !_.includes(
    [
      CURRICULUM_TYPE_MYP,
      CURRICULUM_TYPE_PYP,
      CURRICULUM_TYPE_UBD,
      CURRICULUM_TYPE_DP,
    ],
    curriculumKey
  );
};

export const getUserSortingOptions = (t, type) => {
  const sortByOptions = getUserSortOptions({
    sortOptions: sortByOptionsArray,
    keyToMap: "sortEntity",
    type: type,
  });

  return _.map(sortByOptions, option => {
    const label = _.get(option, "label", "");

    return {
      ...option,
      label: t(label),
    };
  });
};

export const getLearningCourseSortingOptions = t => {
  return _.map(learningCourseSortOptions, option => {
    const label = _.get(option, "label", "");

    return {
      ...option,
      label: t(label),
    };
  });
};

export const getBlueprintCourseSortingOptions = t => {
  return _.map(blueprintCourseSortOptions, option => {
    const label = _.get(option, "label", "");
    return {
      ...option,
      label: t(label),
    };
  });
};

export const getCoursesSortingOptions = t => {
  return _.map(courseSortOptions, option => {
    const label = _.get(option, "label", "");

    return {
      ...option,
      label: t(label),
    };
  });
};

export const getThirdPartyMappingQueryVariables = _.memoize(
  ({ organizationId, service, curriculumId, entityType, academicYearId }) => {
    return {
      id: organizationId,
      input: {
        service,
        entityType,
        curriculumId,
        academicYearId,
      },
    };
  },
  JSON.stringify
);

export const getSubtaskFeedVariables = _.memoize(
  ({
    jobId,
    type,
    subType,
    orderBy,
    first,
    searchText = "",
    grades = [],
    mappingState,
    archivalState = null,
  }) => {
    return {
      filters: {
        type,
        subType,
        orderBy,
        searchString: searchText,
        grades,
        ...(mappingState && mappingState !== "ALL" ? { mappingState } : {}),
        ...(archivalState ? { archivalState } : {}),
      },
      first: first ? first : 200000, //To get non paginated feed
      id: jobId,
    };
  },
  JSON.stringify
);

export const getEntityMappingsQueryVariables = _.memoize(
  ({ curriculumId, service, entityType }) => {
    return {
      input: {
        curriculumId,
        entityType,
        service,
        isSanitizeEnabled: true,
      },
    };
  },
  JSON.stringify
);

export const getExclusionEntityQueryVariables = _.memoize(
  ({ curriculumId, source, type, subType }) => {
    return {
      input: {
        curriculumId,
        source,
        type,
        subType,
      },
    };
  },
  JSON.stringify
);

const getUserArchivalStatus = user => {
  return ACLStore.can(ENABLE_USER_ACADEMIC_YEAR_ARCHIVAL_STATUS)
    ? _.get(user, "isArchivedV2", false)
    : _.get(user, "isArchived", false);
};

export const getFilteredChildNodes = ({
  template,
  service,
  isDevUser,
  devUserSettingsFilter,
  children,
}) => {
  return _.filter(children, child => {
    const childConfig = template[child];

    const isDevUserOnly = _.get(childConfig, "isDevUserOnly", null);

    const permission = _.get(childConfig, "permission", null);

    const isPermitted = permission ? ACLStore.can(permission) : true;

    if (isDevUserOnly && !isDevUser) {
      return false;
    }

    if (!isPermitted) {
      return false;
    }

    // Filter out settings that are only available for the developer user when settings filter is not set to ALL
    if (isDevUser && isDevUserOnly && devUserSettingsFilter?.key !== "ALL") {
      return false;
    }

    const enabledServices = _.get(childConfig, "enabled_services", false);

    const hasCurrentService = _.includes(enabledServices, service);

    if (!enabledServices || hasCurrentService) {
      return true;
    }

    return false;
  });
};

export const getFilteredSettingsNodes = ({
  template,
  service,
  isDevUser,
  devUserSettingsFilter,
}) => {
  const rootNodes = _.get(template, "syncSettings", []);
  return _.reduce(
    rootNodes,
    (result, node) => {
      const nodeConfig = template[node];

      const showOnlyForTheseServices = _.get(
        nodeConfig,
        "showOnlyForTheseServices",
        []
      );

      const hasCurrentService = _.includes(showOnlyForTheseServices, service);

      const isDevOnly = _.get(nodeConfig, "isDevOnly", false);

      if (isDevOnly && !isDevUser) {
        return result;
      }

      // Filter out settings that are only available for the developer user when settings filter is not set to ALL
      if (isDevUser && isDevOnly && devUserSettingsFilter?.key !== "ALL") {
        return result;
      }

      const showCurrent =
        _.isEmpty(showOnlyForTheseServices) ||
        (!_.isEmpty(showOnlyForTheseServices) && hasCurrentService);

      if (!showCurrent) {
        return result;
      }

      const children = _.get(nodeConfig, "children", []);
      const childNodes = getFilteredChildNodes({
        template,
        service,
        isDevUser,
        devUserSettingsFilter,
        children,
      });

      if (!_.isEmpty(childNodes)) {
        result.push({
          node,
          children: childNodes,
        });
      }

      return result;
    },
    []
  );
};

export const getTermMappingTableHeaders = getMemoizedLangFunction(
  ({ t, service }) => {
    const serviceName = getUiNames({ data: service, t });

    const headers = [
      {
        key: "GRADING_PERIOD_TODDLE",
        value: t("integration:toddle_grading_periods"),
        style: { width: "50%" },
      },
      {
        key: "GRADING_PERIOD_THIRD_PARTY",
        value: t("integration:grading_periods_with_label", {
          label: serviceName,
        }),
        style: { width: "50%" },
      },
    ];

    return headers;
  }
);

export const getLocaleForAlertTitle = ({
  isLearningCourseEnabled,
  isAcademicCourseEnabled,
  isBlueprintCoursesEnabled,
}) => {
  if (
    isBlueprintCoursesEnabled &&
    isAcademicCourseEnabled &&
    isLearningCourseEnabled
  ) {
    return "integration:review_modal_archiving_entities_warning_with_count_v4";
  }
  if (isAcademicCourseEnabled && isLearningCourseEnabled) {
    return "integration:review_modal_archiving_entities_warning_with_count_v3";
  }
  if (isLearningCourseEnabled) {
    return "integration:review_modal_archiving_entities_warning_with_count_v2";
  }
  return "integration:review_modal_archiving_entities_warning_with_count";
};

export const getAdminSetupFiltersByEntityTypeForRosterMemoized = _.memoize(
  params => getAdminSetupFiltersByEntityTypeForRoster(params),
  params => JSON.stringify(params)
);

const getAdminSetupFiltersByEntityTypeForRoster = ({
  type,
  courses = [],
  accountAclRoles = [],
  programAclRoles = [],
  showOnlyResultsWithNoProgramACLRoles = false,
  searchText = "",
  templateType,
  curriculumProgramId,
  curriculumProgramIds,
  archivalState,
  designations,
  showOnlyResultsWithNoAssignedClasses,
  searchTextFields,
  grades,
  subjects,
  excludedTeacherIds,
  excludedStudentIds,
  subjectGroups,
  excludedCourseIds,
  excludedStaffIds,
  tags = [],
  yearGroups = [],
  levels = [],
  responseLanguages = [],
  homeroomFilters = {},
  academicYearId,
  academicYearIds,
  excludedGrades,
  roles,
  blockStatus,
  gender,
  firstNationality,
  secondNationality,
  firstLanguage,
  learningCourse = [],
  staff = [],
  createdBy = [],
}) => {
  switch (type) {
    case "SUBJECT":
      return {
        curriculumProgramIds: curriculumProgramId ? [curriculumProgramId] : [],
        searchText,
        grades,
        tags,
        archivalState,
        levels,
        responseLanguages,
      };
    case "GRADE":
    case "SUBJECT_GROUP":
    case "YEAR_GROUPS":
      return {
        curriculumProgramIds: curriculumProgramId
          ? _.isArray(curriculumProgramId)
            ? curriculumProgramId
            : [curriculumProgramId]
          : [],
        academicYearId,
      };
    case "STUDENT":
      return {
        courses,
        searchText,
        ...(showOnlyResultsWithNoAssignedClasses
          ? { showOnlyResultsWithNoAssignedClasses: true }
          : {}),
        ...(curriculumProgramIds
          ? { curriculumProgramIds }
          : curriculumProgramId
          ? { curriculumProgramIds: [curriculumProgramId] }
          : { curriculumProgramIds: [] }),
        archivalState,
        searchTextFields,
        excludedCourseIds,
        excludedStudentIds,
        grades,
        yearGroups,
        ...(!_.isEmpty(homeroomFilters) && { homeroomFilters }),
        ...(academicYearId ? { academicYearId } : {}),
        ...(academicYearIds ? { academicYearIds } : {}),
        excludedGrades,
        blockStatus,
        gender,
        firstNationality,
        secondNationality,
        firstLanguage,
      };
    case "STAFF":
      return {
        courses,
        accountAclRoles,
        programAclRoles,
        showOnlyResultsWithNoProgramACLRoles,
        searchText,
        designations,
        ...(showOnlyResultsWithNoAssignedClasses
          ? { showOnlyResultsWithNoAssignedClasses: true }
          : {}),
        ...(curriculumProgramIds
          ? { curriculumProgramIds }
          : curriculumProgramId
          ? { curriculumProgramIds: [curriculumProgramId] }
          : { curriculumProgramIds: [] }),
        archivalState,
        searchTextFields,
        includeSupportUser: false,
        ...(academicYearIds
          ? { academicYearIds }
          : academicYearId
          ? { academicYearIds: [academicYearId] }
          : {}),
      };
    // case "LEARNING_COURSE":
    case "COURSE":
      return {
        searchText,
        designations,
        archivalState,
        curriculumProgramIds: curriculumProgramId
          ? _.isArray(curriculumProgramId)
            ? curriculumProgramId
            : [curriculumProgramId]
          : [],
        grades,
        subjects,
        excludedTeacherIds,
        excludedStudentIds,
        subjectGroups,
        excludedCourseIds,
        excludedStaffIds,
        ...(academicYearIds
          ? { academicYears: academicYearIds }
          : academicYearId
          ? { academicYears: [academicYearId] }
          : {}),
        excludedGrades,
        roles,
        ...(!_.isEmpty(learningCourse) && {
          includedLearningCourseIds: learningCourse,
        }),
        ...(!_.isEmpty(staff) && { includedTeacherIds: staff }),
        ...(!_.isEmpty(createdBy) && { createdByIds: createdBy }),
      };
    case "BLUEPRINT_COURSE":
    case "LEARNING_COURSE":
      return {
        searchText,
        designations,
        archivalState,
        grades,
        subjects,
        excludedTeacherIds,
        excludedStudentIds,
        subjectGroups,
        excludedCourseIds,
        excludedStaffIds,
        types:
          type === "BLUEPRINT_COURSE"
            ? ["BLUEPRINT_COURSE"]
            : ["TEACHER_COURSE"],
        ...(academicYearId ? { academicYears: [academicYearId] } : {}),
        excludedGrades,
        roles,
      };
  }
};
